import { DocumentNode, useMutation, useQuery } from '@apollo/client';
import { Query } from '@apollo/react-components';
import React, {
  ComponentProps,
  FunctionComponent,
  ReactElement,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  AmazonConfig,
  BigCommerceConfiguration,
  BinLocation,
  Certificate,
  Customer,
  EbayConfig,
  EtsyConfiguration,
  MagentoConfiguration,
  ProductAttributeConfig,
  ProductAuditByField,
  ProductCategory,
  ProductNoteType,
  ShopifyConfiguration,
  Supplier,
  WooCommerceConfiguration,
} from '../../graphql/entities';
import Product from '../../graphql/entities/product/Product';
import mapStateToFormProps from '../../utils/mapStateToFormProps';
import createForm from '../../components/connectedForm/ConnectedForm';
import { default as Form } from '../../components/connectedForm';
import ColumnLabel from '../../components/connectedForm/ColumnLabel';
import * as input from '../../components/connectedForm/inputComponents';
import GridContainer from '../../components/connectedForm/GridContainer';
import LotAttributeConfigList from '../../graphql/queries/product/LotAttributeConfigListQuery.graphql';
import ProductCategoryCheckboxTree from '../../components/connectedForm/ProductCategoryCheckboxTree';
import ChannelList from '../../graphql/queries/product/ChannelListQuery.graphql';
import getIn from '../../components/connectedForm/utils/getIn';
import StockTransactionWorkspace from '../../views/product/ProductsStockTransactionWorkspace';
import DropdownEditor from '../../components/DropdownEditor';
import { useCompany, useProductConfig, useSalesOrderConfig } from '../../utils/FetchConfig';
import { normalizeUpperCase } from '../../utils/Values';
import Formatters from '../../components/grid-addons/formatters';
import productComponentDirectAncestors from '../../graphql/queries/product/ProductComponentDirectAncestors.graphql';
import componentQuery from '../../utils/componentQuery';
import EditableDataGrid from '../../components/EditableDataGrid';
import QueryDropdown from '../../components/connectedForm/QueryDropdown';
import fieldMetaData, { IEnumMeta } from '../../graphql/schema/fieldMetaData';
import SalesInvoiceAnalysis, {
  EmbeddedSalesInvoiceOptions,
} from '../salesLedger/salesInvoiceAnalysis/SalesInvoiceAnalysis';

import SalesOrderLineWorkspace from '../sales/SalesOrderLineWorkspace';
import PurchaseOrderLineWorkspace from '../purchases/PurchaseOrderLineWorkspace';
import AuditByFieldWorkspace from '../company/AuditByFieldWorkspace';
import Label from '../../components/connectedForm/Label/Label';
import { MenuItem } from 'react-contextmenu';
import ProductsStockLotWorkspace from './ProductsStockLotWorkspace';
import setIn from '../../components/connectedForm/utils/setIn';
import ProductAvailability from './ProductAvailability';
import ProductSupplierConfig from './ProductSupplierConfig';
import { getEntryCount } from '../../utils/CountUtil';
import ProductOverviewTab from './ProductOverviewTab';
import { useLocation, useParams } from 'react-router';
import { useTemporarySave } from '../../components/connectedForm/utils/handleTemporarySave';
import ConnectedFormContext from '../../components/Context/ConnectedFormContext';
import AppContext from '../../components/Context/AppContext';
import SalesInvoiceLineWorkspace from '../salesLedger/SalesInvoiceLineWorkspace';
import getQuantityDP from '../../utils/getProductDP';
import { useFormQuery } from '../../graphql/containers/useFormQuery';
import { VolumeFields } from './VolumeFields';
import { ConnectedFormPristinePrompt } from '../../components/connectedForm/ConnectedFormPristinePrompt';
import { FormAlertDetails, FormAlertRow } from '../common/FormAlertsTab';
import BlurTextField from '../../components/connectedForm/BlurTextField';
import { StockLevelTab } from './StockLevelTab';
import { debounce } from 'lodash';

import UserContext from '../../components/Context/UserContext';
import {
  CostPrice,
  CostViewPermission,
  FilterOperation,
  ICompany,
  IEdataDetailsByProductQueryResult,
  IFilterInput,
  IPricingAttributesEmbeddable,
  IProduct,
  IProductComponent,
  IProductConfig,
  IProductMutationResult,
  IProductMutationVariables,
  IProductNotesQueryResult,
  IProductNotesQueryVariables,
  IProductPriceMatrix,
  IProductWarehouseDetail,
  IQueryEdataDetailsByProductArgs,
  ISellingPricesEmbeddable,
  IStockLot,
  Maybe,
  ProductClass,
  ValuationMethod,
  IStockLotMutationResult,
  IStockLotMutationVariables,
  AlertType,
  SalesDefaultUnit,
  PurchaseDefaultUnit,
  IUnitOfMeasureEmbeddable,
} from '../../graphql/schema/p4types';

import { BlurTextFieldType, IBlurTextFieldProps } from '../../components/connectedForm/BlurTextField/BlurTextField';
import { IDropdownProps } from '../../@types/dropdown';
import {
  IEditableDataGridColumnDef,
  IEditableDataGridContextOption,
  IEditableDataGridDropdownOptions,
} from '../../@types/editableDataGridTypes';
import { Dispatch } from 'redux';
import { ICheckboxInputProps } from '../../components/connectedForm/CheckboxInput/CheckboxInput';
import { IAttributeArrayProps } from '../../components/connectedForm/AttributeArray';
import { IEditableDataGridProps } from '../../components/EditableDataGrid/EditableDataGrid';
import GridUploader, { IGridUploaderProps } from '../../components/connectedForm/GridUploader';
import { EntityType } from '../../@types/entityTypes';
import { IConnectedFormProps } from '../../@types/connectedFormTypes';
import CommentRowContext from '../../components/Context/CommentRowContext';
import CheckboxInputEditor from '../../components/CheckboxInputEditor/CheckboxInputEditor';
import { useProductFormActions } from '../../actions/product/ProductFormAction';
import { ProgressIndicator } from '@fluentui/react/lib/ProgressIndicator';
import ConnectedPricingAttributeField from '../../components/ConnectedPricingAttributeField';
import { IDateFieldProps } from '../../components/connectedForm/DateInput/DateInput';
import MemoEditor, { IMemoEditorType, useMemoEditorProps } from '../../components/connectedForm/MemoEditor/MemoEditor';
import ProductNotesWorkspaceQuery from '../../graphql/queries/product/ProductNotesWorkspaceQuery.graphql';
import ProductsStockLotWorkspaceQuery from '../../graphql/queries/product/ProductsStockLotWorkspaceQuery.graphql';

import useEntityCacheUpdate from '../../graphql/hooks/useEntityCacheUpdate';
import { cloneDeep } from '@apollo/client/utilities';
import { NumberRange } from '../../@types/numberRange';
import defaultFilterOptions from '../../components/react-select/utils/defaultFilterOptions';
import { FreeTypeForm } from './FreeTypeForm';
import { Wysiwyg } from '@ogl/lexical-editor';
import AssemblyAttributeConfig from '../../graphql/entities/product/AssemblyAttributeConfig';
import { uuIdGenerator } from '../../utils/UuIdGenerator';
import WebMediaFolderTreespace from '../web/WebMediaFolderTreespace';
import { usePrecision } from '../../utils/hooks';
import { useHandleEditorSubmit, useStaticBlocks } from '../web/WebPageForm';
import { Paths } from '../../@types/keyOfNested';
import StockLotMutation from '../../graphql/mutations/product/StockLotMutation.graphql';
import { useComponentQueryErrorHandling } from '../../utils/parseErrorResponse';
import getClientMutationId from '../../utils/getClientMutationId';
import normalizeVariables, { normalizeFields } from '../../graphql/schema/normalizeVariables';
import { useP4ToastController } from '../system/NotificationsArea';
import EdataProductDetails from './EdataProductDetails';
import EdataDetailsByProduct from '../../graphql/queries/product/EdataDetailsByProductQuery';
import { Radio, RadioGroup } from '@fluentui/react-components';
import CurrencyContext from '../../components/Context/CurrencyContext';
import Material from '../../graphql/entities/product/Material';
import SubMaterial from '../../graphql/entities/product/SubMaterial';
import MaterialColour from '../../graphql/entities/product/MaterialColour';
import Country from '../../graphql/entities/company/Country';
import Dropdown from '../../components/connectedForm/Dropdown';
import { Button } from '../../components/connectedForm/Button';
import RowRenderer from '../../components/react-data-grid/Row';
import { round, RoundingModes } from '../../utils/NumberUtils';
import { useConfirmationRequired } from '../../components/modals/ConfirmationPrompt';

const LazyCommentRowRenderer = React.lazy(() => import('../../components/EditableDataGrid/CommentRowRenderer'));
const LazyPurchaseRequirementDetailWorkspace = React.lazy(
  () => import('../../views/purchases/PurchaseRequirementDetailWorkspace')
);
const LazyAssemblyWorkspace = React.lazy(() => import('../../views/product/AssemblyWorkspace'));
const LazyProductAllocationWorkspace = React.lazy(() => import('../../views/product/ProductAllocationWorkspace'));
const LazyReservationWorkspace = React.lazy(() => import('../../views/product/ReservationWorkspace'));
const LazyStockCommitmentWorkspace = React.lazy(() => import('../../views/product/StockCommitmentWorkspace'));

const AnyCheckboxEditor = CheckboxInputEditor as any;

const NumericFormatter = Formatters.NumericFormatter;
const DateFormatter = Formatters.DateFormatter;

const {
  default: defaultQuery,
  fragment,
  formTitle,
  dropdown,
  workspaceUrl,
  update,
  instance,
  temporaryUpdate,
  type,
  temporary,
} = Product;

const PRODUCT_FORM_DATA_STORE_ID = 'componentWindow';

type IProductPredefinedFilters = {
  transactionPredefinedFilters?: Array<IFilterInput>;
  stockLotsPredefinedFilters?: Array<IFilterInput>;
  productAuditPredefinedFilters?: Array<IFilterInput>;
  predefinedAnalysisFilters?: Array<{ dimension: string; id: string }>;
};

export const PRODUCT_MATERIAL_DETAIL_COLUMNS = [
  {
    name: 'Material',
    key: 'material',
    editable: true,
    resizable: true,
    editor: <DropdownEditor />,
    formatter: Formatters.DropdownFormatter,
    options: {
      dropdown: {
        ...Material.dropdown,
      },
    },
  },
  {
    name: 'Sub Material',
    key: 'subMaterial',
    editable: true,
    resizable: true,
    editor: <DropdownEditor />,
    formatter: Formatters.DropdownFormatter,
    options: {
      dropdown: {
        ...SubMaterial.dropdown,
      },
    },
  },
  {
    name: 'Colour',
    key: 'materialColour',
    editable: true,
    resizable: true,
    editor: <DropdownEditor />,
    formatter: Formatters.DropdownFormatter,
    options: {
      dropdown: {
        ...MaterialColour.dropdown,
      },
    },
  },
  {
    name: 'Origin',
    key: 'origin',
    editable: true,
    resizable: true,
    editor: <DropdownEditor />,
    formatter: Formatters.DropdownFormatter,
    options: {
      dropdown: {
        ...Country.dropdown,
      },
    },
  },
  {
    name: 'Weight',
    key: 'weight',
    editable: true,
    resizable: true,
    cellClass: 'rightAlign',
    formatter: NumericFormatter,
  },
  {
    name: 'g/Kg',
    key: 'weightType',
    editable: true,
    resizable: true,
    editor: <DropdownEditor options={fieldMetaData.WeightType.enumValues} enum={true} />,
    formatter: Formatters.DropdownFormatter,
  },
  {
    name: 'Recycled (%)',
    key: 'percentageRecycled',
    editable: true,
    resizable: true,
    cellClass: 'rightAlign',
    formatter: NumericFormatter,
  },
  {
    name: 'BOM',
    key: 'bom',
    editable: true,
    resizable: true,
    formatter: Formatters.CheckFormatter,
    editor: <AnyCheckboxEditor />,
  },
  {
    name: 'Packaging Use',
    key: 'packagingUse',
    editable: true,
    resizable: true,
    editor: <DropdownEditor options={fieldMetaData.PackagingUse.enumValues} enum={true} />,
    formatter: Formatters.DropdownFormatter,
  },
  {
    name: 'Waste Type',
    key: 'wasteType',
    editable: true,
    resizable: true,
    editor: <DropdownEditor options={fieldMetaData.WasteType.enumValues} enum={true} />,
    formatter: Formatters.DropdownFormatter,
  },
];

const createProductFilters: (id: number) => IProductPredefinedFilters = (id) => {
  return {
    transactionPredefinedFilters: [
      {
        filterOperation: FilterOperation.Equals,
        filterParameters: {
          field: 'product.id',
          value: `${id}`,
        },
      },
    ],
    stockLotsPredefinedFilters: [
      {
        filterOperation: FilterOperation.Equals,
        filterParameters: {
          field: 'stock.product.id',
          value: `${id}`,
        },
      },
    ],
    productAuditPredefinedFilters: [
      {
        filterOperation: FilterOperation.Equals,
        filterParameters: {
          field: 'id',
          value: `${id}`,
        },
      },
    ],
    predefinedAnalysisFilters: [
      {
        dimension: 'PRODUCT',
        id: `${id}`,
      },
    ],
  };
};

const PriceField = ({
  priceIndex,
  priceMatrix,
  productConfig,
  sellingPrices,
}: {
  priceIndex: NumberRange<1, 10>;
  priceMatrix: Maybe<IProductPriceMatrix>;
  productConfig: Maybe<IProductConfig>;
  sellingPrices: Maybe<ISellingPricesEmbeddable>;
}) => {
  const title = productConfig?.[`sellingPriceTitle${priceIndex}`];
  if (!title || title === '') return null;

  const priceMatrixCalculation = priceMatrix?.[`price${priceIndex}Calculation` as keyof IProductPriceMatrix];
  const retailPreview = sellingPrices?.[`sellingPriceRetailPreview${priceIndex}` as keyof ISellingPricesEmbeddable];

  const readOnlyMessage = `Calculated from ${getIn(
    priceMatrix,
    `price${priceIndex}Calculation.priceBase`
  )} via price matrix for '${getIn(priceMatrix, 'name')}'`;

  const quantityBreakField =
    priceIndex > 1 ? (
      <Form.Field<IBlurTextFieldProps>
        dataid={`sellingPrices.sellingPriceBreak${priceIndex}`}
        component={BlurTextField}
        label="break"
        readOnly={!!priceMatrixCalculation}
      />
    ) : null;

  return (
    <Form.Row>
      <Form.Fieldset cols={1 / 6}>
        <Label>{title}</Label>
      </Form.Fieldset>
      <Form.Fieldset cols={2 / 6}>
        <Form.Field<IBlurTextFieldProps>
          dataid={`sellingPrices.sellingPrice${priceIndex}`}
          component={BlurTextField}
          temporary={true}
          readOnly={!!priceMatrixCalculation}
          readOnlyMessage={readOnlyMessage}
          includeLabel={false}
          type={BlurTextFieldType.BaseCurrency}
          valueType={'sales'}
        />
      </Form.Fieldset>
      <Form.Fieldset cols={2 / 6}>
        <Form.Field<IBlurTextFieldProps>
          dataid={`sellingPrices.sellingPriceRetail${priceIndex}`}
          component={BlurTextField}
          temporary={true}
          includeLabel={false}
          placeholder={`Calculated (${retailPreview?.toFixed(2)})`}
          type={BlurTextFieldType.BaseCurrency}
          valueType={'sales'}
        />
      </Form.Fieldset>
      <Form.Fieldset cols={1 / 6}>{quantityBreakField}</Form.Fieldset>
    </Form.Row>
  );
};

const PricingAttributeField = ({
  attributeIndex,
  pricingAttributes,
}: {
  attributeIndex: NumberRange<1, 10>;
  pricingAttributes: Maybe<IPricingAttributesEmbeddable>;
}) => {
  if (!pricingAttributes) return null;

  return <ConnectedPricingAttributeField attributeIndex={attributeIndex} />;
};

export const ProductHeader = ({
  onCancel,
  values,
  actionButtons,
  modalClose,
  onSubmit,
}: {
  onCancel?: () => void;
  values: IProduct;
  actionButtons: Array<any>;
  modalClose: boolean;
  onSubmit?: () => void;
}) => {
  const [productFormActions, productFormActionModals] = useProductFormActions();

  return (
    <>
      {productFormActionModals}
      <Form.Header
        entityHdr="description"
        entityHdr2="reference"
        entityHdrStyle="small"
        component={Form.HeaderBlock}
        statusFlags={[
          {
            key: 'template',
            status: [
              {
                value: true,
                style: 'info',
                title: 'Template',
              },
            ],
          },
          {
            key: 'preview',
            status: [
              {
                value: true,
                style: 'info',
                title: 'Preview',
              },
            ],
          },
          {
            key: 'productOrigin',
            status: [
              {
                value: 'SPECIAL',
                style: 'info',
                title: 'Special Order',
              },
              {
                value: 'LUCKINS',
                style: 'info',
                title: 'Luckins',
              },
              {
                value: 'IMPORT',
                style: 'info',
                title: 'Imported',
              },
            ],
          },
        ]}
        onCancel={onCancel}
        archive={true}
        allowRestoreOption={true}
        noDeleteOption={!values.id}
        actionButtons={[...(actionButtons || []), ...productFormActions]}
        isModal={modalClose /*Modal Close should be a reasonably consistent check for being nested in a modal.*/}
        onSubmit={onSubmit}
      />
    </>
  );
};

export const ProductReferenceField = ({
  referenceFormatInUse,
  values,
}: {
  referenceFormatInUse?: Maybe<boolean>;
  values: IProduct;
}) => {
  const { specialOrder, template, specialOrderTemplateReference } = values;
  const isSpecialOrder = !template && specialOrder;

  return (
    <Form.Field<IBlurTextFieldProps>
      dataid="reference"
      component={BlurTextField}
      label="Reference"
      normalize={normalizeUpperCase}
      spellCheck="false"
      temporary={true}
      placeholder={isSpecialOrder && !referenceFormatInUse ? `${specialOrderTemplateReference}...` : ''}
      readOnly={(isSpecialOrder && referenceFormatInUse) as boolean}
      hint={
        isSpecialOrder && referenceFormatInUse
          ? 'Special Order Product reference will be automatically generated upon save.'
          : undefined
      }
    />
  );
};

const CategoryCheckboxTree = ({
  change,
  values,
  readOnly,
}: {
  change: ComponentProps<typeof ProductCategoryCheckboxTree>['change'];
  values: IProduct;
  readOnly?: boolean;
}) => {
  if (readOnly) return null;
  return (
    <>
      <Form.Legend label="Categories" />
      <Form.Row style={{ maxHeight: '0' }}>
        <ProductCategoryCheckboxTree query={ProductCategory.list} change={change} values={values} />
      </Form.Row>
    </>
  );
};

const CostPrices = ({ template }: { template: Maybe<boolean> }) => {
  const userContext = useContext(UserContext);
  const { values } = useContext(ConnectedFormContext);
  if (userContext.currentUser.companyUser.costViewPermission === CostViewPermission.None) return null;

  const renderCost = userContext.currentUser.companyUser.costViewPermission === CostViewPermission.All && !template;
  //Allow editing haggle cost on new record creation if costViewPermission is set to 'Haggle'.
  const renderHaggleCost =
    userContext.currentUser.companyUser.costViewPermission === CostViewPermission.Haggle && !template;

  const priceMatrix = values?.activeProductPriceMatrix || {};

  const filterOption = (option: IEnumMeta<ValuationMethod>) => {
    for (const valuationOption of fieldMetaData.ValuationMethod.enumValues) {
      if (option?.value === valuationOption?.value) {
        return !(valuationOption.filter && values && valuationOption.filter.leveled !== values?.leveled);
      }
    }
    return false;
  };

  const valuationMethod =
    renderCost || template ? (
      <Form.Field<IDropdownProps>
        dataid="valuationMethod"
        component={input.Dropdown}
        label="Valuation Method"
        filterOption={filterOption}
        readOnly={!!template}
      />
    ) : null;

  const latestAndValuationCost = renderCost ? (
    <>
      <Form.Field<IBlurTextFieldProps>
        dataid="valuationCostPrice"
        component={BlurTextField}
        label="Valuation Cost Price"
        temporary={true}
        type={BlurTextFieldType.BaseCurrency}
        valueType={'cost'}
      />
      <Form.Field<IBlurTextFieldProps>
        dataid="latestCostPrice"
        component={BlurTextField}
        label="Latest Cost Price"
        temporary={true}
        readOnly={priceMatrix.latestCostCalculation}
        readOnlyMessage={
          priceMatrix.latestCostCalculation?.priceBase === undefined
            ? undefined
            : `Calculated from ${priceMatrix.latestCostCalculation.priceBase}`
        }
        type={BlurTextFieldType.BaseCurrency}
        valueType={'cost'}
      />
    </>
  ) : null;

  const haggleCost =
    renderCost || renderHaggleCost ? (
      <Form.Field<IBlurTextFieldProps>
        dataid="haggleCostPrice"
        component={BlurTextField}
        label={!renderCost && renderHaggleCost ? 'Cost Price' : 'Haggle Cost Price'}
        temporary={true}
        readOnly={priceMatrix.haggleCostCalculation}
        readOnlyMessage={
          priceMatrix.haggleCostCalculation?.priceBase === undefined
            ? undefined
            : `Calculated from ${priceMatrix.haggleCostCalculation.priceBase}`
        }
        type={BlurTextFieldType.BaseCurrency}
        valueType={'cost'}
      />
    ) : null;

  return (
    <Form.Row>
      <Form.Fieldset cols={1 / 2}>
        <Form.Legend label="Cost Prices" />
        {valuationMethod}
        {latestAndValuationCost}
        {haggleCost}
      </Form.Fieldset>
    </Form.Row>
  );
};

const MiniProductForm = (props: ComponentProps<typeof ProductForm>) => {
  const { onCancel, actionButtons, modalClose, submit: handleSubmit } = props;
  const values: IProduct = props.values;
  const referenceFormatInUse: boolean = !!props.referenceFormatInUse;

  const connectedForm = useContext(ConnectedFormContext);
  const { change } = connectedForm;
  const dispatch = useDispatch();

  const initialSupplierDetails = values?.alternateSupplierDetails?.[0];
  const supplier = initialSupplierDetails?.supplier ?? null;
  const valuationCostPrice = values?.valuationCostPrice;

  const temporarySave = useTemporarySave();

  const { currency } = useContext(CurrencyContext);

  const updateSupplier = useCallback(
    (newSupplier) => {
      dispatch(change?.('alternateSupplierDetails.0.supplier', newSupplier));
      if (!initialSupplierDetails?.uuid) {
        dispatch(change?.('alternateSupplierDetails.0.uuid', uuIdGenerator()));
      }
      temporarySave();
    },
    [change, dispatch, initialSupplierDetails?.uuid, temporarySave]
  );

  const updateSupplierCostPrice = useCallback(
    (newSupplierCostPrice) => {
      if (newSupplierCostPrice?.endsWith('.')) {
        return;
      }

      dispatch(change?.('valuationCostPrice', newSupplierCostPrice));
      dispatch(change?.('latestCostPrice', newSupplierCostPrice));
      dispatch(change?.('alternateSupplierDetails.0.costPrices.costPrice1', newSupplierCostPrice));

      if (!initialSupplierDetails?.uuid) {
        dispatch(change?.('alternateSupplierDetails.0.uuid', uuIdGenerator()));
      }
    },
    [change, dispatch, initialSupplierDetails?.uuid]
  );

  return (
    <Form.FormContainer ignoreClickOutside={true} data-testid={props['data-testid'] ?? 'product-mini-form-container'}>
      <CurrencyContext.Provider
        value={{ currency: props.values?.alternateSupplierDetails?.[0]?.supplier?.currency ?? currency }}
      >
        <ProductHeader
          onCancel={onCancel}
          values={values}
          actionButtons={actionButtons}
          modalClose={modalClose}
          onSubmit={handleSubmit}
        />
        <Form.Form>
          <Form.Row>
            <Form.Fieldset cols={1}>
              <ProductReferenceField values={values} referenceFormatInUse={referenceFormatInUse} />
              <Form.Field<IBlurTextFieldProps>
                dataid="description"
                component={BlurTextField}
                label="Description"
                autoFocus={true}
              />
              <QueryDropdown
                dataid={`alternateSupplierDetails.0.supplier`}
                component={input.Dropdown}
                label={'Supplier'}
                {...(Supplier.dropdown as any)}
                temporary={true}
                value={supplier}
                onChange={updateSupplier}
              />
              <BlurTextField
                label="Supplier cost price"
                value={valuationCostPrice}
                onChange={updateSupplierCostPrice}
                enableOnChange
                data-testid={'mini-product-form-supplier-cost-price'}
                type={BlurTextFieldType.Currency}
              />
            </Form.Fieldset>
          </Form.Row>
        </Form.Form>
      </CurrencyContext.Provider>
    </Form.FormContainer>
  );
};

const useProductFormColumnMeta: (options: {
  onAddLineCommentClick: (value: any, close: () => void, rowIdx: number) => void;
  getPrecision: (rowData: any, path?: string) => number;
}) => {
  [key: string]: Array<IEditableDataGridColumnDef>;
} = ({ onAddLineCommentClick, getPrecision }) => {
  const connectedForm = useContext(ConnectedFormContext);
  const dispatch = useDispatch();
  const { change, values } = connectedForm;

  const onChannelDelete = (
    dataId:
      | 'channelsEbay'
      | 'channelsAmazon'
      | 'channelsShopify'
      | 'channelsWooCommerce'
      | 'channelsEtsy'
      | 'channelsBigCommerce'
      | 'channelsMagento',
    close: () => void,
    rowIdx: number
  ) => {
    close();
    let rows;
    const channels = (values as IProduct)[dataId] ?? [];
    if (getIn(channels, `[${rowIdx}].id`)) {
      rows = setIn(channels, `[${rowIdx}].deleted`, true);
    } else {
      rows = channels.filter((item, idx) => idx !== rowIdx);
    }
    dispatch(change(dataId, rows));
  };

  const onEbayChannelDelete: IEditableDataGridContextOption['onClick'] = (value, close, rowIdx) => {
    onChannelDelete('channelsEbay', close, rowIdx);
  };
  const onAmazonChannelDelete: IEditableDataGridContextOption['onClick'] = (value, close, rowIdx) => {
    onChannelDelete('channelsAmazon', close, rowIdx);
  };
  const onShopifyChannelDelete: IEditableDataGridContextOption['onClick'] = (value, close, rowIdx) => {
    onChannelDelete('channelsShopify', close, rowIdx);
  };
  const onWooCommerceChannelDelete: IEditableDataGridContextOption['onClick'] = (value, close, rowIdx) => {
    onChannelDelete('channelsWooCommerce', close, rowIdx);
  };
  const onEtsyChannelDelete: IEditableDataGridContextOption['onClick'] = (value, close, rowIdx) => {
    onChannelDelete('channelsEtsy', close, rowIdx);
  };
  const onBigCommerceChannelDelete: IEditableDataGridContextOption['onClick'] = (value, close, rowIdx) => {
    onChannelDelete('channelsBigCommerce', close, rowIdx);
  };
  const onMagentoChannelDelete: IEditableDataGridContextOption['onClick'] = (value, close, rowIdx) => {
    onChannelDelete('channelsMagento', close, rowIdx);
  };

  const onDeleteClick: IEditableDataGridContextOption['onClick'] = useCallback(
    (value, close, rowIdx) => {
      close?.();
      dispatch(change(`components.${rowIdx}.deleted`, true));
    },
    [change, dispatch]
  );

  const onDeleteComponentReturnClick: IEditableDataGridContextOption['onClick'] = useCallback(
    (value, close, rowIdx) => {
      close?.();
      dispatch(change(`componentReturns.${rowIdx}.deleted`, true));
    },
    [change, dispatch]
  );

  const productOptions: IEditableDataGridDropdownOptions = useMemo(
    () => ({
      ...(Product as any),
      dropdown: {
        ...Product.dropdown,
        predefinedFilters: [
          {
            filterOperation: FilterOperation.Custom,
            filterParameters: {
              field: 'template',
            },
          },
          {
            filterOperation: FilterOperation.NotEquals,
            filterParameters: {
              field: 'productClass',
              value: ProductClass.FreeType,
            },
          },
        ],
      },
    }),
    []
  );
  const costPermission = useSelector((store: any) => store.app?.fullUserDetails?.companyUser?.costViewPermission);
  const orderConfig = useSalesOrderConfig();
  const productConfig = useProductConfig();
  const costPricePrecision = usePrecision('cost');

  const componentColumnMeta: Array<IEditableDataGridColumnDef> = useMemo(() => {
    const baseComponentColumnMeta: Array<IEditableDataGridColumnDef> = [
      {
        name: 'Product',
        key: 'component',
        editable: true,
        editor: <DropdownEditor />,
        options: productOptions,
        formatter: 'dropdown',
        width: 250,
        resizable: false,
      },
      {
        name: 'Description',
        key: 'component.description',
        editable: false,
        resizable: true,
      },
      {
        name: 'Cost Unit Description',
        key: 'component.costUnitDescription',
        editable: false,
        resizable: true,
      },
      {
        name: 'Quantity',
        key: 'quantity',
        formatter: NumericFormatter,
        editable: true,
        resizable: true,
        cellClass: 'rightAlign',
        width: 110,
        options: {
          format: { decimals: (rowData) => getQuantityDP(rowData, 'component') },
        },
      },
      {
        name: 'Free Stock',
        key: 'component.freeStock',
        formatter: NumericFormatter,
        editable: false,
        resizable: true,
        cellClass: 'rightAlign',
        width: 110,
        options: {
          format: { decimals: (rowData) => getQuantityDP(rowData, 'component') },
        },
      },
      {
        name: 'Sequence',
        key: 'sequence',
        formatter: NumericFormatter,
        editable: true,
        resizable: true,
        cellClass: 'rightAlign',
        width: 90,
        options: {
          format: { decimals: 0 },
        },
      },
    ];

    if (costPermission && costPermission !== CostViewPermission.None) {
      let key = '';

      switch (costPermission) {
        case CostViewPermission.All: {
          const isAssembly = values?.productClass === ProductClass.Assembly;
          const useValuation = isAssembly
            ? productConfig?.componentCost === CostPrice.Valuation
            : orderConfig?.costForMargin === CostPrice.Valuation;

          key = useValuation ? 'component.valuationCostPrice' : 'component.latestCostPrice';
          break;
        }
        case CostViewPermission.Haggle: {
          key = 'component.haggleCostPrice';
          break;
        }
        default: {
          throw new Error('Unsupported cost permission provided');
        }
      }

      baseComponentColumnMeta.push({
        name: 'Cost Per Unit',
        key: key,
        formatter: NumericFormatter,
        editable: false,
        resizable: true,
        cellClass: 'rightAlign',
        width: 90,
        options: {
          format: { decimals: costPricePrecision },
        },
      });
    }

    baseComponentColumnMeta.push({
      name: '',
      key: '',
      editable: true,
      resizable: true,
      formatter: Formatters.ContextOptionsFormatter,
      options: [
        {
          label: 'Delete',
          onClick: onDeleteClick,
        },
        {
          label: 'Add Comment',
          onClick: onAddLineCommentClick,
        },
      ],
      cellClass: 'centerAlign',
      width: 75,
    });

    return baseComponentColumnMeta;
  }, [
    costPermission,
    costPricePrecision,
    onAddLineCommentClick,
    onDeleteClick,
    orderConfig?.costForMargin,
    productOptions,
  ]);

  const componentReturnColumnMeta: Array<IEditableDataGridColumnDef> = useMemo(() => {
    const baseComponentReturnColumnMeta: Array<IEditableDataGridColumnDef> = [
      {
        name: 'Product',
        key: 'product',
        editable: true,
        editor: <DropdownEditor />,
        options: productOptions,
        formatter: 'dropdown',
        width: 250,
        resizable: false,
      },
      {
        name: 'Description',
        key: 'product.description',
        editable: false,
        resizable: true,
      },
      {
        name: 'Cost Unit Description',
        key: 'product.costUnitDescription',
        editable: false,
        resizable: true,
      },
      {
        name: 'Quantity',
        key: 'quantity',
        formatter: NumericFormatter,
        editable: true,
        resizable: true,
        cellClass: 'rightAlign',
        width: 110,
        options: {
          format: { decimals: (rowData) => getQuantityDP(rowData, 'component') },
        },
      },
      {
        name: 'Free Stock',
        key: 'product.freeStock',
        formatter: NumericFormatter,
        editable: false,
        resizable: true,
        cellClass: 'rightAlign',
        width: 110,
        options: {
          format: { decimals: (rowData) => getQuantityDP(rowData, 'component') },
        },
      },
    ];

    if (costPermission && costPermission !== CostViewPermission.None) {
      let key = '';

      switch (costPermission) {
        case CostViewPermission.All: {
          key = orderConfig?.costForMargin === 'VALUATION' ? 'product.valuationCostPrice' : 'product.latestCostPrice';
          break;
        }
        case CostViewPermission.Haggle: {
          key = 'product.haggleCostPrice';
          break;
        }
        default: {
          throw new Error('Unsupported cost permission provided');
        }
      }

      baseComponentReturnColumnMeta.push({
        name: 'Cost Per Unit',
        key: key,
        formatter: NumericFormatter,
        editable: false,
        resizable: true,
        cellClass: 'rightAlign',
        width: 90,
        options: {
          format: { decimals: getPrecision },
        },
      });
    }

    baseComponentReturnColumnMeta.push({
      name: '',
      key: '',
      editable: true,
      resizable: true,
      formatter: Formatters.ContextOptionsFormatter,
      options: [
        {
          label: 'Delete',
          onClick: onDeleteComponentReturnClick,
        },
      ],
      cellClass: 'centerAlign',
      width: 75,
    });

    return baseComponentReturnColumnMeta;
  }, [costPermission, getPrecision, onDeleteComponentReturnClick, orderConfig?.costForMargin, productOptions]);

  const binLocationFilter = (rowData: IProductWarehouseDetail): IFilterInput => {
    return {
      filterOperation: FilterOperation.Equals,
      filterParameters: {
        field: 'warehouse.id',
        value: rowData.warehouse?.id,
      },
    };
  };

  const warehouseColumnMeta: Array<IEditableDataGridColumnDef> = useMemo(() => {
    const baseWarehouseColumnMeta: Array<IEditableDataGridColumnDef> = [
      {
        name: 'Warehouse',
        key: 'warehouse.description',
        editable: false,
        resizable: true,
        widthFactor: 4,
      },
      {
        name: 'Held?',
        widthFactor: 1,
        key: 'held',
        editable: true,
        resizable: true,
        formatter: Formatters.CheckFormatter,
        editor: <AnyCheckboxEditor />,
      },
      {
        name: 'Minimum Level',
        key: 'minimumLevel',
        editable: true,
        resizable: true,
        cellClass: 'rightAlign',
        formatter: NumericFormatter,
        options: {
          format: { decimals: getPrecision },
        },
        widthFactor: 2,
      },
      {
        name: 'Optimum Level',
        key: 'optimumLevel',
        editable: true,
        resizable: true,
        cellClass: 'rightAlign',
        formatter: NumericFormatter,
        options: {
          format: { decimals: getPrecision },
        },
        widthFactor: 2,
      },
      {
        name: 'Maximum Level',
        key: 'maximumLevel',
        editable: true,
        resizable: true,
        cellClass: 'rightAlign',
        formatter: NumericFormatter,
        options: {
          format: { decimals: getPrecision },
        },
        widthFactor: 2,
      },
      {
        name: 'Movements Since Stock Take',
        key: 'movementsSinceLastStockTake',
        editable: false,
        resizable: true,
        cellClass: 'rightAlign',
        formatter: NumericFormatter,
        options: {
          format: { decimals: 0 },
        },
        widthFactor: 2,
      },
      {
        name: 'Last Issue',
        key: 'dateOfLastIssue',
        editable: false,
        resizable: true,
        cellClass: 'rightAlign',
        formatter: DateFormatter,
        options: {
          includeTime: true,
        },
        widthFactor: 2,
      },
      {
        name: 'Last Receipt',
        key: 'dateOfLastReceipt',
        editable: false,
        resizable: true,
        cellClass: 'rightAlign',
        formatter: DateFormatter,
        options: {
          includeTime: true,
        },
        widthFactor: 2,
      },
      {
        name: 'Default Bin Location',
        key: 'defaultBinLocation',
        editable: true,
        editor: <DropdownEditor customFilter={binLocationFilter} />,
        options: BinLocation as any,
        formatter: 'dropdown',
        widthFactor: 2,
        resizable: false,
      },
      {
        name: 'Stock Take Last Created',
        key: 'lastStockTakeCreated',
        editable: false,
        resizable: true,
        cellClass: 'rightAlign',
        formatter: DateFormatter,
        widthFactor: 2,
      },
      {
        name: 'Stock Take Last Updated',
        key: 'lastStockTakeUpdated',
        editable: false,
        resizable: true,
        cellClass: 'rightAlign',
        formatter: DateFormatter,
        widthFactor: 2,
      },
      {
        name: 'Bin - Min Level',
        key: 'defaultBinMinimumLevel',
        editable: true,
        resizable: true,
        cellClass: 'rightAlign',
        formatter: NumericFormatter,
        options: {
          format: { decimals: getPrecision },
        },
        widthFactor: 2,
      },
      {
        name: 'Bin - Opt Level',
        key: 'defaultBinOptimumLevel',
        editable: true,
        resizable: true,
        cellClass: 'rightAlign',
        formatter: NumericFormatter,
        options: {
          format: { decimals: getPrecision },
        },
        widthFactor: 2,
      },
      {
        name: 'Bin - Max Level',
        key: 'defaultBinMaximumLevel',
        editable: true,
        resizable: true,
        cellClass: 'rightAlign',
        formatter: NumericFormatter,
        options: {
          format: { decimals: getPrecision },
        },
        widthFactor: 2,
      },
      {
        name: 'Stock Take After',
        key: 'stockTakeAfter',
        editable: true,
        resizable: true,
        cellClass: 'rightAlign',
        formatter: NumericFormatter,
        options: {
          format: { decimals: 0 },
        },
        widthFactor: 2,
      },
    ];

    switch (costPermission) {
      case CostViewPermission.All: {
        baseWarehouseColumnMeta.push({
          name: 'Valuation Cost Price',
          key: 'averageCostPrice',
          editable: true,
          resizable: true,
          cellClass: 'rightAlign',
          formatter: NumericFormatter,
          options: {
            format: { decimals: costPricePrecision },
          },
          widthFactor: 2,
        });
        baseWarehouseColumnMeta.push({
          name: 'Latest Cost Price',
          key: 'latestCostPrice',
          editable: true,
          resizable: true,
          cellClass: 'rightAlign',
          formatter: NumericFormatter,
          options: {
            format: { decimals: costPricePrecision },
          },
          widthFactor: 2,
        });
      }

      /* FALLTHROUGH */
      case CostViewPermission.Haggle:
        baseWarehouseColumnMeta.push({
          name: 'Haggle Cost Price',
          key: 'haggleCostPrice',
          editable: true,
          resizable: true,
          cellClass: 'rightAlign',
          formatter: NumericFormatter,
          options: {
            format: { decimals: costPricePrecision },
          },
          widthFactor: 2,
        });
        break;
      default:
        break;
    }

    return baseWarehouseColumnMeta;
  }, [costPermission, costPricePrecision, getPrecision]);

  const productAncestorColumnMeta: Array<IEditableDataGridColumnDef> = [
    {
      name: 'Product',
      key: 'ancestor.reference',
      editable: false,
    },
    {
      name: 'Description',
      key: 'ancestor.description',
      editable: false,
    },
    {
      name: 'Quantity',
      key: 'quantityInAncestor',
      editable: false,
    },
    {
      name: 'On Sales Order',
      key: 'quantityOnOrder',
      editable: false,
    },
    {
      key: 'ancestor.id',
      name: 'Ancestor',
      editable: false,
      resizable: false,
      sortable: false,
      width: 70,
      formatter: Formatters.LinkFormatter,
      options: {
        url: '/Products/Product/Details/',
        entity: { type: connectedForm.entity?.type } as EntityType,
        editInNewTab: true,
      },
      draggable: false,
    },
  ];

  const alternativeProductColumnMeta: Array<IEditableDataGridColumnDef> = [
    {
      name: 'Product',
      key: 'product',
      editable: true,
      editor: <DropdownEditor />,
      options: Product,
      formatter: 'dropdown',
      resizable: true,
    },
    {
      name: 'Preferred/Replaces',
      key: 'preferred',
      editable: true,
      resizable: true,
      formatter: Formatters.CheckFormatter,
      editor: <AnyCheckboxEditor />,
    },
    {
      name: 'Type',
      key: 'type',
      formatter: Formatters.DropdownFormatter,
      options: {
        type: 'AlternativeProductType',
        dropdown: {
          isEnum: true,
          clearable: false,
        },
      } as IEditableDataGridDropdownOptions,
      editor: <DropdownEditor />,
      resizable: true,
      editable: true,
    },
  ];

  const ebayChannelColumnMeta: Array<IEditableDataGridColumnDef> = [
    {
      name: 'Configuration',
      key: 'ebayConfiguration',
      editable: true,
      resizable: true,
      editor: <DropdownEditor />,
      formatter: Formatters.DropdownFormatter,
      options: EbayConfig,
    },
    {
      name: 'Alias',
      key: 'alias',
      editable: true,
      resizable: true,
    },
    {
      name: 'Item ID',
      key: 'itemId',
      editable: true,
      resizable: true,
    },
    {
      name: 'Quantity',
      key: 'quantity',
      editable: true,
      resizable: true,
      formatter: Formatters.NumericFormatter,
      options: {
        format: { decimals: getPrecision },
        suppressZeroes: true,
      },
    },
    {
      name: 'Excl Stock Updates',
      key: 'excludeFromStockUpdates',
      editable: true,
      width: 120,
      editor: 'CheckboxInput',
      formatter: Formatters.BooleanFormatter,
    },
    {
      name: '',
      key: '',
      editable: true,
      resizable: true,
      formatter: Formatters.ContextOptionsFormatter,
      options: [
        {
          icon: 'cancel',
          onClick: onEbayChannelDelete,
        },
      ],
      cellClass: 'centerAlign',
      width: 40,
    },
  ];

  const amazonChannelColumnMeta: Array<IEditableDataGridColumnDef> = [
    {
      name: 'Configuration',
      key: 'amazonConfiguration',
      editable: true,
      resizable: true,
      editor: <DropdownEditor />,
      formatter: Formatters.DropdownFormatter,
      options: AmazonConfig,
    },
    {
      name: 'Alias',
      key: 'alias',
      editable: true,
      resizable: true,
    },
    {
      name: 'Quantity',
      key: 'quantity',
      editable: true,
      resizable: true,
      formatter: Formatters.NumericFormatter,
      options: {
        format: { decimals: getPrecision },
        suppressZeroes: true,
      },
    },
    {
      name: 'Fulfillment Latency',
      key: 'latency',
      editable: true,
      resizable: true,
      formatter: Formatters.NumericFormatter,
      options: {
        format: { decimals: 0 },
        suppressZeroes: true,
      },
    },
    {
      name: 'Excl Stock Updates',
      key: 'excludeFromStockUpdates',
      editable: true,
      width: 120,
      editor: 'CheckboxInput',
      formatter: Formatters.BooleanFormatter,
    },
    {
      name: '',
      key: '',
      editable: true,
      resizable: true,
      formatter: Formatters.ContextOptionsFormatter,
      options: [
        {
          icon: 'cancel',
          onClick: onAmazonChannelDelete,
        },
      ],
      cellClass: 'centerAlign',
      width: 40,
    },
  ];

  const shopifyChannelColumnMeta: Array<IEditableDataGridColumnDef> = [
    {
      name: 'Configuration',
      key: 'shopifyConfiguration',
      editable: true,
      resizable: true,
      editor: <DropdownEditor />,
      formatter: Formatters.DropdownFormatter,
      options: ShopifyConfiguration,
    },
    {
      name: 'Alias',
      key: 'alias',
      editable: true,
      resizable: true,
    },
    {
      name: 'Quantity',
      key: 'quantity',
      editable: true,
      resizable: true,
      formatter: Formatters.NumericFormatter,
      options: {
        format: { decimals: getPrecision },
        suppressZeroes: true,
      },
    },
    {
      name: 'Excl Stock Updates',
      key: 'excludeFromStockUpdates',
      editable: true,
      width: 120,
      editor: 'CheckboxInput',
      formatter: Formatters.BooleanFormatter,
    },
    {
      name: '',
      key: '',
      editable: true,
      resizable: true,
      formatter: Formatters.ContextOptionsFormatter,
      options: [
        {
          icon: 'cancel',
          onClick: onShopifyChannelDelete,
        },
      ],
      cellClass: 'centerAlign',
      width: 40,
    },
  ];

  const wooCommerceChannelColumnMeta: Array<IEditableDataGridColumnDef> = [
    {
      name: 'Configuration',
      key: 'wooCommerceConfiguration',
      editable: true,
      resizable: true,
      editor: <DropdownEditor />,
      formatter: Formatters.DropdownFormatter,
      options: WooCommerceConfiguration,
    },
    {
      name: 'Alias',
      key: 'alias',
      editable: true,
      resizable: true,
    },
    {
      name: 'Quantity',
      key: 'quantity',
      editable: true,
      resizable: true,
      formatter: Formatters.NumericFormatter,
      options: {
        format: { decimals: getPrecision },
        suppressZeroes: true,
      },
    },
    {
      name: 'Excl Stock Updates',
      key: 'excludeFromStockUpdates',
      editable: true,
      width: 120,
      editor: 'CheckboxInput',
      formatter: Formatters.BooleanFormatter,
    },
    {
      name: '',
      key: '',
      editable: true,
      resizable: true,
      formatter: Formatters.ContextOptionsFormatter,
      options: [
        {
          icon: 'cancel',
          onClick: onWooCommerceChannelDelete,
        },
      ],
      cellClass: 'centerAlign',
      width: 40,
    },
  ];

  const etsyChannelColumnMeta: Array<IEditableDataGridColumnDef> = [
    {
      name: 'Configuration',
      key: 'etsyConfiguration',
      editable: true,
      resizable: true,
      editor: <DropdownEditor />,
      formatter: Formatters.DropdownFormatter,
      options: EtsyConfiguration,
    },
    {
      name: 'Alias',
      key: 'alias',
      editable: true,
      resizable: true,
    },
    {
      name: 'Quantity',
      key: 'quantity',
      editable: true,
      resizable: true,
      formatter: Formatters.NumericFormatter,
      options: {
        format: { decimals: getPrecision },
        suppressZeroes: true,
      },
    },
    {
      name: 'Excl Stock Updates',
      key: 'excludeFromStockUpdates',
      editable: true,
      width: 120,
      editor: 'CheckboxInput',
      formatter: Formatters.BooleanFormatter,
    },
    {
      name: '',
      key: '',
      editable: false,
      resizable: true,
      formatter: Formatters.ContextOptionsFormatter,
      options: [
        {
          icon: 'cancel',
          onClick: onEtsyChannelDelete,
        },
      ],
      cellClass: 'centerAlign',
      width: 40,
    },
  ];

  const bigCommerceChannelColumnMeta: Array<IEditableDataGridColumnDef> = [
    {
      name: 'Configuration',
      key: 'bigCommerceConfiguration',
      editable: true,
      resizable: true,
      editor: <DropdownEditor />,
      formatter: Formatters.DropdownFormatter,
      options: BigCommerceConfiguration,
    },
    {
      name: 'Alias',
      key: 'alias',
      editable: true,
      resizable: true,
    },
    {
      name: 'Quantity',
      key: 'quantity',
      editable: true,
      resizable: true,
      formatter: Formatters.NumericFormatter,
      options: {
        format: { decimals: getPrecision },
        suppressZeroes: true,
      },
    },
    {
      name: 'Excl Stock Updates',
      key: 'excludeFromStockUpdates',
      editable: true,
      width: 120,
      editor: 'CheckboxInput',
      formatter: Formatters.BooleanFormatter,
    },
    {
      name: '',
      key: '',
      editable: true,
      resizable: true,
      formatter: Formatters.ContextOptionsFormatter,
      options: [
        {
          icon: 'cancel',
          onClick: onBigCommerceChannelDelete,
        },
      ],
      cellClass: 'centerAlign',
      width: 40,
    },
  ];

  const magentoChannelColumnMeta: Array<IEditableDataGridColumnDef> = [
    {
      name: 'Configuration',
      key: 'magentoConfiguration',
      editable: true,
      resizable: true,
      editor: <DropdownEditor />,
      formatter: Formatters.DropdownFormatter,
      options: MagentoConfiguration,
    },
    {
      name: 'Alias',
      key: 'alias',
      editable: true,
      resizable: true,
    },
    {
      name: 'Quantity',
      key: 'quantity',
      editable: true,
      resizable: true,
      formatter: Formatters.NumericFormatter,
      options: {
        format: { decimals: getPrecision },
        suppressZeroes: true,
      },
    },
    {
      name: 'Excl Stock Updates',
      key: 'excludeFromStockUpdates',
      editable: true,
      width: 120,
      editor: 'CheckboxInput',
      formatter: Formatters.BooleanFormatter,
    },
    {
      name: '',
      key: '',
      editable: true,
      resizable: true,
      formatter: Formatters.ContextOptionsFormatter,
      options: [
        {
          icon: 'cancel',
          onClick: onMagentoChannelDelete,
        },
      ],
      cellClass: 'centerAlign',
      width: 40,
    },
  ];

  return {
    componentColumnMeta,
    warehouseColumnMeta,
    alternativeProductColumnMeta,
    ebayChannelColumnMeta,
    amazonChannelColumnMeta,
    shopifyChannelColumnMeta,
    wooCommerceChannelColumnMeta,
    etsyChannelColumnMeta,
    bigCommerceChannelColumnMeta,
    magentoChannelColumnMeta,
    productAncestorColumnMeta,
    componentReturnColumnMeta,
  };
};

export type IProductFormProps = {
  values: IProduct;
  dispatch: Dispatch;
  change: (
    field: string,
    value: any,
    mutationId?: string
  ) => {
    type: string;
    meta: { form: string; field: string; touch: boolean; mutationId?: string };
    payload: any;
  };
  onChange: Function; //change & onChange??
  connectedForm: any; //In future would prefer to make ProductForm a function component and access via ConnectedFormContext.
  entityType?: string;
  miniForm?: boolean;
  productFilters: IProductPredefinedFilters;
  newProduct?: boolean;
  onCancel?: () => void;
  actionButtons?: Array<any>;
  modalClose?: boolean;

  /**
   *   Optional onClose function - Use in conjunction with 'modalClose' (Otherwise the form will navigate 'back' by standard).
   */
  onClose?: (result?: IProduct) => void;
  activeTabId?: string;
  readOnly?: boolean;
  productConfig?: Maybe<IProductConfig>;
  initialized?: boolean;
  pageView?: boolean;
  referenceFormatInUse?: boolean;
  formData?: any;

  /**
   * Optional data-testid to go on the container element of the form. May be used for Modal views onto the form (Not expected for the regular view).
   */
  'data-testid'?: string;

  /**
   * Trigger a form submit - Provided by ConnectedForm (Note - In most cases it's preferable to use 'usePerformSave()' for manual submission triggers.
   */
  submit?: () => void;
  match?: any;
};

const StandardUnit = () => {
  const connectedForm = useContext(ConnectedFormContext);
  const values = connectedForm.values as IProduct;

  const standardPurchaseUnit = values.defaultAlternativeUnitPurchasing;
  const standardSalesUnit = values.defaultAlternativeUnitSelling;

  const standardPurchaseUnitChecked = standardPurchaseUnit == PurchaseDefaultUnit.StandardPurchaseUnit;
  const standardSalesUnitChecked = standardSalesUnit == SalesDefaultUnit.StandardSalesUnit;

  return (
    <Form.Row>
      <Form.Fieldset cols={8 / 12} collapsed={true} />
      <Form.Fieldset cols={1 / 12} collapsed={true}>
        Standard
      </Form.Fieldset>
      <Form.Fieldset cols={1 / 12} collapsed={true}>
        <Radio
          checked={standardSalesUnitChecked}
          onChange={(ev, data) => {
            const checked = ev.target.checked;
            if (checked) {
              connectedForm.change('defaultAlternativeUnitSelling', SalesDefaultUnit.StandardSalesUnit);
            }
          }}
        />
      </Form.Fieldset>
      <Form.Fieldset cols={1 / 12} collapsed={true}>
        <Radio
          checked={standardPurchaseUnitChecked}
          onChange={(ev, data) => {
            const checked = ev.target.checked;
            if (checked) {
              connectedForm.change('defaultAlternativeUnitPurchasing', PurchaseDefaultUnit.StandardPurchaseUnit);
            }
          }}
        />
      </Form.Fieldset>
    </Form.Row>
  );
};

const AlternateUnit = ({
  unit,
  defaultUnitEnabled,
}: {
  unit: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10;
  defaultUnitEnabled: boolean;
}) => {
  const connectedForm = useContext(ConnectedFormContext);
  const values = connectedForm.values as IProduct;

  const showUnit = (unit: NumberRange<1, 10>) => {
    if (unit === 1) {
      return true;
    }

    const desc = values?.[`alternateSellingUnit${unit}`]?.description;
    const prevDesc =
      values?.[`alternateSellingUnit${unit - 1}` as `alternateSellingUnit${NumberRange<1, 9>}`]?.description;
    return !!(desc || prevDesc);
  };

  if (!showUnit(unit)) return null;

  const baseUnit = values.sellingUnitDescription !== null ? values.sellingUnitDescription : '...';
  let altUnit = values?.[`alternateSellingUnit${unit}`]?.description;

  const standardPurchaseUnit = values?.defaultAlternativeUnitPurchasing;
  const standardSalesUnit = values?.defaultAlternativeUnitSelling;

  const standardPurchaseUnitChecked = standardPurchaseUnit == `ALT_PURCHASE_UNIT_${unit}`;
  const standardSalesUnitChecked = standardSalesUnit == `ALT_SALES_UNIT_${unit}`;

  if (!altUnit) altUnit = '...';

  const directionOptions = [
    {
      value: 'FROM_BASE_UNIT',
      label: `${baseUnit}(s) per ${altUnit}`,
    },
    {
      value: 'TO_BASE_UNIT',
      label: `${altUnit}(s) per ${baseUnit}`,
    },
  ];
  return (
    <Form.Row>
      <Form.Fieldset cols={2 / 12} collapsed={true}>
        <Form.Field<IBlurTextFieldProps>
          dataid={`alternateSellingUnit${unit}.description`}
          component={BlurTextField}
          includeLabel={false}
          inputCols={1}
        />
      </Form.Fieldset>
      <Form.Fieldset cols={1 / 12} collapsed={true}>
        <Form.Field<IBlurTextFieldProps>
          dataid={`alternateSellingUnit${unit}.quantityFactor`}
          component={BlurTextField}
          includeLabel={false}
          inputCols={1}
        />
      </Form.Fieldset>
      <Form.Fieldset cols={3 / 12} collapsed={true}>
        <Form.Field<IDropdownProps>
          dataid={`alternateSellingUnit${unit}.factorDirection`}
          component={input.Dropdown}
          includeLabel={false}
          rawLabel={true}
          options={directionOptions}
          dynamicOptions={true}
          enum={true}
          valueKey="value"
          labelKey="label"
          inputCols={1}
        />
      </Form.Fieldset>
      <Form.Fieldset cols={1 / 12} collapsed={true}>
        <Form.Field<IBlurTextFieldProps>
          dataid={`alternateSellingUnit${unit}.precision`}
          component={BlurTextField}
          includeLabel={false}
          inputCols={1}
        />
      </Form.Fieldset>
      <Form.Fieldset cols={2 / 12} collapsed={true}>
        <Form.Field<IBlurTextFieldProps>
          dataid={`alternateSellingUnit${unit}.barcode`}
          component={BlurTextField}
          includeLabel={false}
          inputCols={1}
        />
      </Form.Fieldset>
      {defaultUnitEnabled && (
        <>
          <Form.Fieldset cols={1 / 12} collapsed={true}>
            <Radio
              checked={standardSalesUnitChecked}
              onChange={(ev, data) => {
                const checked = ev.target.checked;
                if (checked) {
                  connectedForm.change('defaultAlternativeUnitSelling', `ALT_SALES_UNIT_${unit}`);
                }
              }}
            />
          </Form.Fieldset>
          <Form.Fieldset cols={1 / 12} collapsed={false}>
            <Radio
              checked={standardPurchaseUnitChecked}
              onChange={(ev, data) => {
                const checked = ev.target.checked;
                if (checked) {
                  connectedForm.change('defaultAlternativeUnitPurchasing', `ALT_PURCHASE_UNIT_${unit}`);
                }
              }}
            />
          </Form.Fieldset>
        </>
      )}
    </Form.Row>
  );
};

export type EprContextType = {
  selectedEprUnit?: Maybe<IUnitOfMeasureEmbeddable>;
};

const EprContext = React.createContext({} as EprContextType);

const EprRowRender = (props) => {
  const { selectedEprUnit } = useContext(EprContext);
  if (props?.row?.sellingUnitDescription && props?.row?.sellingUnitDescription !== selectedEprUnit?.description) {
    return <></>;
  }
  return <RowRenderer {...props} />;
};

const ProductForm = (props: IProductFormProps) => {
  const [addCommentToRowIdx, setAddCommentToRowIdx] = useState(-1);
  const [componentsTabProductId, setComponentsTabProductId] = useState<Maybe<number>>(null);

  const [selectedStockLot, setSelectedStockLot] = useState<Maybe<IStockLot>>(null);
  const [selectedEprUnit, setSelectedEprUnit] = useState<Maybe<IUnitOfMeasureEmbeddable>>(null);

  useEffect(() => {
    props.values?.productMaterialDetails?.forEach((productMaterialDetail, index) => {
      if (selectedEprUnit?.description && !productMaterialDetail?.sellingUnitDescription) {
        dispatch(props.change(`productMaterialDetails.${index}.sellingUnitDescription`, selectedEprUnit?.description));
      }
    });
  }, [props.values?.productMaterialDetails?.length]);

  const eprUnitOptions = useMemo(() => {
    setSelectedEprUnit(props.values?.standardSellingUnit);
    return [
      props.values?.standardSellingUnit,
      props.values?.alternateSellingUnit1,
      props.values?.alternateSellingUnit2,
      props.values?.alternateSellingUnit3,
      props.values?.alternateSellingUnit4,
      props.values?.alternateSellingUnit5,
      props.values?.alternateSellingUnit6,
      props.values?.alternateSellingUnit7,
      props.values?.alternateSellingUnit8,
      props.values?.alternateSellingUnit9,
      props.values?.alternateSellingUnit10,
    ].filter((unit) => unit && unit.description);
  }, [
    props.values?.standardSellingUnit,
    props.values?.alternateSellingUnit1,
    props.values?.alternateSellingUnit2,
    props.values?.alternateSellingUnit3,
    props.values?.alternateSellingUnit4,
    props.values?.alternateSellingUnit5,
    props.values?.alternateSellingUnit6,
    props.values?.alternateSellingUnit7,
    props.values?.alternateSellingUnit8,
    props.values?.alternateSellingUnit9,
    props.values?.alternateSellingUnit10,
  ]);

  const { dispatchSimpleToast } = useP4ToastController();

  const connectedForm = useContext(ConnectedFormContext);
  const wysiwygRef = useRef();

  const dispatch = useDispatch();
  const costPermission = useSelector((store: any) => store.app?.fullUserDetails?.companyUser?.costViewPermission);
  const productConfig = useProductConfig();
  const company = useCompany();
  const orderConfig = useSalesOrderConfig();
  const windsorFrameworkEnabled = company?.windsorFrameworkEnabled;

  const onSubmit = useHandleEditorSubmit(wysiwygRef, connectedForm, 'webDescription');

  const handleQueryError = useComponentQueryErrorHandling();
  const [stockLotMutation] = useMutation<IStockLotMutationResult, IStockLotMutationVariables>(StockLotMutation, {
    onError: handleQueryError,
    refetchQueries: [ProductsStockLotWorkspaceQuery],
  });

  const { staticBlocks, debouncedSearch, onMergedContentRequested } = useStaticBlocks();

  const memoEditorProps = useMemoEditorProps<IProductNotesQueryResult, IProductNotesQueryVariables>(
    ProductNotesWorkspaceQuery,
    IMemoEditorType.Product,
    {
      predefinedFilters: [
        {
          filterOperation: FilterOperation.Equals,
          filterParameters: {
            field: 'product.id',
            value: props.values.id,
          },
        },
      ],
    }
  );

  const { fullUserDetails } = useContext(AppContext);

  const getPrecision = useCallback<(rowData?: any, path?: string) => number>(() => {
    return connectedForm.values?.precision || 0;
  }, [connectedForm.values?.precision]);

  const onAddLineCommentClick = useCallback<(value: any, close: Maybe<() => void>, rowIdx: number) => void>(
    (value, close, rowIdx) => {
      close?.();
      setAddCommentToRowIdx(rowIdx);
    },
    []
  );

  const duplicateMaterialDetails = useCallback(() => {
    const baseUnitProductMaterialDetails = props.values?.productMaterialDetails?.filter((productMaterialDetail) => {
      return productMaterialDetail?.sellingUnitDescription === props.values?.standardSellingUnit?.description;
    });

    const clonedBaseUnitProductMaterialDetails = cloneDeep(baseUnitProductMaterialDetails);

    clonedBaseUnitProductMaterialDetails?.forEach((productMaterialDetail) => {
      if (productMaterialDetail) {
        productMaterialDetail.sellingUnitDescription = selectedEprUnit?.description;
        productMaterialDetail.id = null;
        productMaterialDetail.uuid = uuIdGenerator();
        if (productMaterialDetail.weight) {
          if (selectedEprUnit?.quantityFactor) {
            if (selectedEprUnit.factorDirection === 'FROM_BASE_UNIT') {
              productMaterialDetail.weight = productMaterialDetail.weight * selectedEprUnit.quantityFactor;
            } else {
              productMaterialDetail.weight = productMaterialDetail.weight / selectedEprUnit.quantityFactor;
            }
          }

          if (selectedEprUnit?.precision !== null && selectedEprUnit?.precision !== undefined) {
            productMaterialDetail.weight = round(
              productMaterialDetail.weight,
              RoundingModes.DOWN,
              selectedEprUnit.precision
            );
          }
        }
      }
    });

    const updatedProductMaterialDetails = [
      ...cloneDeep(props.values?.productMaterialDetails),
      ...clonedBaseUnitProductMaterialDetails,
    ];

    if (updatedProductMaterialDetails) {
      dispatch(props.change(`productMaterialDetails`, updatedProductMaterialDetails));
    }
  }, [
    props.values?.productMaterialDetails,
    props.values?.standardSellingUnit?.description,
    props.change,
    dispatch,
    selectedEprUnit?.description,
    selectedEprUnit?.quantityFactor,
    selectedEprUnit?.precision,
  ]);

  const {
    componentColumnMeta,
    warehouseColumnMeta,
    alternativeProductColumnMeta,
    ebayChannelColumnMeta,
    amazonChannelColumnMeta,
    shopifyChannelColumnMeta,
    wooCommerceChannelColumnMeta,
    etsyChannelColumnMeta,
    bigCommerceChannelColumnMeta,
    magentoChannelColumnMeta,
    productAncestorColumnMeta,
    componentReturnColumnMeta,
  } = useProductFormColumnMeta({ onAddLineCommentClick, getPrecision });

  const tabPanelRef = useRef<any>();

  const values = props.values;

  const temporarySave = useTemporarySave();

  const { onClick: onConfirmDuplicateMaterialDetails, confirmationModal } = useConfirmationRequired(
    duplicateMaterialDetails,
    'Confirm Copy Material Details',
    `Please confirm if you want to copy material details from '${props.values?.standardSellingUnit?.description}' to '${selectedEprUnit?.description}'.`
  );

  const eDataResult = useQuery<IEdataDetailsByProductQueryResult, IQueryEdataDetailsByProductArgs>(
    EdataDetailsByProduct,
    {
      errorPolicy: 'all',
      fetchPolicy: 'network-only',
      variables: { productId: values.id },
      skip: !connectedForm.initialized || !values.id,
    }
  );

  const eDataProductId = !eDataResult.loading && eDataResult.data?.data ? eDataResult.data.data.edataProductId : null;

  //Delete supersession/alternative product
  const onRowDeleteClick = (rowIdx: number) => {
    close();
    let rows;
    const { supersessions } = values;
    if (supersessions?.[rowIdx]?.id) {
      dispatch(props.change(`supersessions.${rowIdx}.deleted`, true));
      temporarySave();
    } else {
      rows = supersessions?.filter((item, idx) => idx !== rowIdx);
      dispatch(props.change('supersessions', rows));
    }
  };

  const renderActionButtons = ({ rowData, rowIdx }: { rowData: IProductComponent; rowIdx: number }) => {
    const renderedButtons: Array<ReactElement> = [];

    if (!rowData.comment) {
      renderedButtons.push(
        <MenuItem
          key={`actionButtonComment_${rowIdx}`}
          onClick={(e) => {
            e.preventDefault();
            onAddLineCommentClick(null, null, rowIdx);
          }}
        >
          Add Comment
        </MenuItem>
      );
    }
    return renderedButtons;
  };

  const templateFilter: () => Array<IFilterInput> = () => {
    return [
      {
        filterOperation: FilterOperation.Equals,
        filterParameters: {
          field: 'template',
          value: 'true',
        },
      },
    ];
  };

  const templateToForm = (value: IProduct) => {
    const vars = { id: value.id };
    //TODO - Replace with useLazyQuery
    componentQuery(defaultQuery, vars, true, 'network-only').then(({ response: data }: any) => {
      dispatch((props.change as any)(false, data.defaultRecord, false, false));
    });
  };

  const defineColumnMeta = () => {
    const customerOptions = { ...Customer };
    if (customerOptions.dropdown) {
      customerOptions.dropdown.clearable = false;
      customerOptions.dropdown.selectModal = 'CustomerWorkspace';
    }

    return [
      {
        name: 'Customer',
        key: 'customer',
        editable: true,
        editor: <DropdownEditor />,
        options: customerOptions,
        formatter: 'dropdown',
        width: 200,
        resizable: false,
      },
      {
        name: 'Part Number',
        key: 'partNumber',
        editable: true,
        resizable: true,
      },
      {
        name: 'Description',
        key: 'description',
        editable: true,
        resizable: true,
      },
      {
        name: 'Certificate',
        key: 'certificate',
        editor: <DropdownEditor />,
        options: Certificate,
        editable: true,
        width: 200,
        resizable: true,
        formatter: 'dropdown',
      },
    ];
  };

  const deleteCustomerPartNumber = (rowIdx: number) => {
    const dataid = `customerPartNumbers.${rowIdx}.deleted`;
    dispatch(props.change(dataid, !getIn(values, dataid)));
  };

  // eslint-disable-next-line complexity

  const {
    id,
    template,
    productClass,
    preview,
    specialReferenceConfig,
    pricingAttributes,
    specialOrder,
    nonReplenishable,
    useSupplementaryUnits,
  } = values;
  const referenceFormatInUse = !!specialReferenceConfig?.referenceFormatInUse;

  if (productClass === ProductClass.FreeType) {
    return <FreeTypeForm {...props} />;
  } else if (props.miniForm) {
    return <MiniProductForm {...props} referenceFormatInUse={referenceFormatInUse} />;
  }

  const priceMatrix: Maybe<IProductPriceMatrix> = values?.activeProductPriceMatrix || ({} as any);

  const {
    transactionPredefinedFilters,
    stockLotsPredefinedFilters,
    productAuditPredefinedFilters,
    predefinedAnalysisFilters,
  } = props.productFilters;

  const templateHint = template ? 'This product is a template. This field cannot be changed.' : null;

  const attachmentsCount = getEntryCount(values.attachments);

  const existingProduct = !props.newProduct && preview !== true;

  const { onCancel, actionButtons, modalClose } = props;

  const showExistingProductDetails = !props.newProduct && productClass !== 'PROCESS' && !preview;

  const stockLotsWorkspaceColumns: Array<Paths<IStockLot>> = [
    'stock.binLocation.location' as Paths<IStockLot>,
    'level',
    'freeStock',
    'allocated',
    'issued',
    'attachments',
  ];
  if (costPermission === CostViewPermission.All) {
    stockLotsWorkspaceColumns.push('averageCostPrice');
  }
  if (nonReplenishable) {
    stockLotsWorkspaceColumns.push(...(['originalLevel', 'consumed'] as Array<Paths<IStockLot>>));
  }

  const onAttachmentChange = debounce((value) => {
    let attachments = cloneDeep(selectedStockLot.attachments);

    if (value?.updated?.deleted) {
      if (attachments && value.rowIdx != null) {
        attachments.splice(value.rowIdx, 1);
      }
    } else if (value?.updated?.comment) {
      attachments[value.rowIdx].comment = value?.updated?.comment;
    } else {
      attachments = value;
    }

    stockLotMutation({
      variables: {
        input: {
          clientMutationId: getClientMutationId(),
          stockLot: normalizeVariables(
            {
              ...selectedStockLot,
              attachments: attachments,
            },
            ['__typename', 'levelValuation', 'allocationValuation', 'freeStock', 'consumed']
          ),
        },
      },
    }).then(({ data: response }) => {
      if (response?.stockLotUpdate?.result) {
        setSelectedStockLot(response.stockLotUpdate.result as IStockLot);
        dispatchSimpleToast('Save Successful', `Attachment has been saved.`, AlertType.Success);
      }
    });
  }, 500);

  return (
    <div data-testid={props['data-testid'] ?? 'product-form-container'}>
      {confirmationModal}
      <ConnectedFormPristinePrompt />
      <Form.FormContainer ignoreClickOutside>
        <ProductHeader
          onCancel={onCancel}
          values={values}
          actionButtons={actionButtons}
          modalClose={modalClose}
          onSubmit={onSubmit}
        />
        <Form.Form>
          <Form.TabPanel
            ref={tabPanelRef}
            formName={Product.type}
            active={props.activeTabId || (existingProduct ? 'overview' : 'main')}
          >
            <Form.TabContent id="overview" label="Overview" shouldRender={existingProduct}>
              <ProductOverviewTab {...props} id={values.id} tabPanelRef={tabPanelRef} sidebar={false} />
            </Form.TabContent>
            <Form.TabContent id="main" label="General">
              <Form.Row>
                {id !== undefined && id === null && !template && !specialOrder ? (
                  <Form.Fieldset cols={1}>
                    <Form.Legend label="Template" />
                    <QueryDropdown
                      value=""
                      valueKey="id"
                      label="Template"
                      customFilter={templateFilter}
                      onChange={templateToForm}
                      {...dropdown}
                      placeholder=""
                      selectModal={null}
                      preLoad={true}
                      data-testid={'product-form-template'}
                    />
                  </Form.Fieldset>
                ) : null}
                <Form.Fieldset cols={4 / 7}>
                  <Form.Legend label="Product Information" />
                  <ProductReferenceField values={values} referenceFormatInUse={referenceFormatInUse} />
                  <Form.Field<IBlurTextFieldProps>
                    dataid="oldReference"
                    component={BlurTextField}
                    label="Old Reference"
                    normalize={normalizeUpperCase}
                    spellCheck="false"
                    readOnly={true}
                    hint={'Imported product reference from previous/other system.'}
                    hideWhenEmpty={true}
                  />
                  <Form.Field<IBlurTextFieldProps> dataid="barcode" component={BlurTextField} label="Barcode" />
                  <Form.Field<IBlurTextFieldProps>
                    dataid="description"
                    component={BlurTextField}
                    label="Default Description"
                  />
                  <Form.Field<IBlurTextFieldProps>
                    dataid="salesDescription"
                    component={BlurTextField}
                    label="Sales Description"
                  />
                  <Form.Field<IDropdownProps>
                    dataid="productClass"
                    component={input.Dropdown}
                    label="Product Class"
                    filterOptions={(
                      options: Array<IEnumMeta<ProductClass>>,
                      filterValueInput,
                      excludeOptionsInput,
                      props
                    ) => {
                      const filtered = defaultFilterOptions(options, filterValueInput, excludeOptionsInput, props);
                      //Do not allow FREETYPE if not enabled via feature flag or permit conversion of existing products into FREETYPE products.
                      if (!fullUserDetails?.flags?.freeTypeLinesEnabled || values.id) {
                        return filtered.filter((value) => value.value !== ProductClass.FreeType);
                      }
                      return filtered;
                    }}
                  />
                  <Form.Field<IDropdownProps> dataid="certificate" component={input.Dropdown} label="Certificate" />
                  <Form.Field dataid="boxLabelsRequired" component={input.Checkbox} label="Box Labels Required" />
                  <Form.Field dataid="obsolete" component={input.Checkbox} label="Obsolete" />
                  <Form.Field<IBlurTextFieldProps>
                    dataid="transferQuantityIncrement"
                    component={BlurTextField}
                    label="Transfer Quantity Increment"
                  />
                  {values?.productClass === ProductClass.Kit && orderConfig?.shipTheoryPasswordSet ? (
                    <Form.Field
                      dataid="shipTheoryCompleteKit"
                      component={input.Checkbox}
                      label="ShipTheory Complete Kit?"
                      hint="If ticked, when dispatching through ShipTheory this item will appear as a single item. If unticked, each component
                      dispatched will appear instead."
                    />
                  ) : null}
                  <Form.Legend label="Product class" />
                  {values?.productClass === ProductClass.Assembly ? (
                    <>
                      <Form.Field<IBlurTextFieldProps>
                        dataid="minimumProductionQuantity"
                        type={BlurTextFieldType.Number}
                        component={BlurTextField}
                        label="Minimum Production Quantity"
                      />
                      <Form.Field<IBlurTextFieldProps>
                        dataid="productionIncrement"
                        type={BlurTextFieldType.Number}
                        component={BlurTextField}
                        label="Production Increment"
                      />
                    </>
                  ) : null}
                </Form.Fieldset>
                <Form.Fieldset cols={3 / 7}>
                  <Form.Legend label="Other Information" />
                  <Form.Field<IBlurTextFieldProps>
                    dataid="createdBy"
                    component={BlurTextField}
                    label="Created By"
                    readOnly={true}
                  />
                  <Form.Field<IDateFieldProps>
                    dataid="createdAt"
                    component={input.DateField}
                    label="Date/Time Created"
                    dateFormat="iii do MMM yyyy HH:mm"
                    readOnly={true}
                  />
                  <Form.Field<IBlurTextFieldProps>
                    dataid="updatedBy"
                    component={BlurTextField}
                    label="Last Updated By"
                    readOnly={true}
                  />
                  <Form.Field<IDateFieldProps>
                    dataid="updatedAt"
                    component={input.DateField}
                    label="Date/Time Updated"
                    dateFormat="iii do MMM yyyy HH:mm"
                    readOnly={true}
                  />
                </Form.Fieldset>
                {template ? (
                  <>
                    <Form.Fieldset cols={1 / 2}>
                      <Form.Legend label="Template Information" />
                      <Form.Field<ICheckboxInputProps>
                        dataid="specialOrder"
                        component={input.Checkbox}
                        temporary={true}
                        label="Special Order"
                        hint={
                          'Enable to allow dynamic generation of products from the template/line during order entry.'
                        }
                      />
                      {specialOrder ? (
                        <>
                          <Form.Field<ICheckboxInputProps>
                            dataid={'specialReferenceConfig.referenceFormatInUse'}
                            component={input.Checkbox}
                            temporary={true}
                            label={'Pre-formatted Reference?'}
                          />
                          {referenceFormatInUse ? (
                            <>
                              <Form.Legend label="Prefix" />
                              <Form.Field<IBlurTextFieldProps>
                                dataid="specialReferenceConfig.prefix"
                                component={BlurTextField}
                                label="Prefix"
                              />
                              <Form.Legend label="Alpha Section" />
                              <Form.Field<IBlurTextFieldProps>
                                dataid="specialReferenceConfig.alphaLength"
                                component={BlurTextField}
                                label="Length"
                              />
                              <Form.Legend label="Numeric Section" />
                              <Form.Field<IBlurTextFieldProps>
                                dataid="specialReferenceConfig.numericLength"
                                component={BlurTextField}
                                label="Length"
                              />
                              <Form.Field<ICheckboxInputProps>
                                dataid="specialReferenceConfig.numericPadded"
                                component={input.Checkbox}
                                label="Padded?"
                              />
                            </>
                          ) : null}
                        </>
                      ) : null}
                    </Form.Fieldset>
                  </>
                ) : null}
              </Form.Row>
            </Form.TabContent>
            {eDataProductId ? (
              <Form.TabContent id="edata" label="EData">
                <EdataProductDetails providedId={eDataProductId} />
              </Form.TabContent>
            ) : null}
            <Form.TabContent id="attributes" label="Attributes" permissionReference={'PRODUCTS_ATTRIBUTES_VIEW'}>
              <Form.Row>
                <Form.Fieldset cols={1 / 2}>
                  <Form.Legend label="Product Attributes" />
                  <Form.Field<IAttributeArrayProps>
                    dataid="attributeValues"
                    component={input.AttributeArray}
                    label="Attributes"
                    query={ProductAttributeConfig.list}
                    temporary={true}
                    type={'product'}
                  />
                  <Form.Legend label="Lot Attributes" />
                  <Form.Field<any>
                    dataid="lotAttributes"
                    component={input.SelectList}
                    query={LotAttributeConfigList}
                    type="selectlist"
                    optionLabelField="title"
                    showHighlight={false}
                    predefinedFilters={[
                      {
                        filterOperation: FilterOperation.Equals,
                        filterParameters: {
                          field: 'internal',
                          value: 'false',
                        },
                      },
                    ]}
                  />
                </Form.Fieldset>
                <Form.Fieldset cols={1 / 2}>
                  {values?.productClass === ProductClass.Assembly ? (
                    <>
                      <Form.Legend label="Assembly Attributes" />
                      <Form.Field<IAttributeArrayProps>
                        dataid="assemblyAttributeValues"
                        component={input.AttributeArray}
                        label="Assembly Attributes"
                        query={AssemblyAttributeConfig.list}
                        temporary={true}
                        type="assembly"
                      />
                    </>
                  ) : null}
                  <CategoryCheckboxTree change={props.change} values={values} readOnly={props.readOnly} />
                </Form.Fieldset>
              </Form.Row>
            </Form.TabContent>
            <Form.TabContent id="costPrice" label="Cost Prices">
              <Form.Row>
                <Form.Fieldset cols={1 / 2}>
                  <Form.Legend label="Cost Unit" />
                  <Form.Field<IBlurTextFieldProps>
                    dataid="costUnitQuantity"
                    component={BlurTextField}
                    type={BlurTextFieldType.Number}
                    label="Unit Quantity"
                  />
                  <Form.Field<IBlurTextFieldProps>
                    dataid="costUnitDescription"
                    component={BlurTextField}
                    label="Unit Description"
                  />
                </Form.Fieldset>
              </Form.Row>
              <CostPrices template={template} />
              <Form.Row>
                <Form.Fieldset cols={1 / 2}>
                  <Form.Legend label="Cost Price Review" />
                  <Form.Field<IBlurTextFieldProps>
                    dataid="costPriceReviewDays"
                    component={input.BlurTextField}
                    decimals={0}
                    datatype="numeric"
                    label={'Number of days since last purchase'}
                  />
                </Form.Fieldset>
              </Form.Row>
            </Form.TabContent>
            <Form.TabContent id="prices" label="Selling Prices">
              <Form.Row>
                <Form.Fieldset cols={1 / 2}>
                  <Form.Legend label="Selling Unit" />
                  <Form.Field<IBlurTextFieldProps>
                    dataid="sellingUnitQuantity"
                    component={BlurTextField}
                    label="Unit Quantity"
                    type={BlurTextFieldType.Number}
                    decimals={getPrecision()}
                  />
                  <Form.Field<IBlurTextFieldProps>
                    dataid="sellingUnitDescription"
                    component={BlurTextField}
                    label="Unit Description"
                  />
                </Form.Fieldset>
                <Form.Fieldset cols={1 / 2}>
                  <Form.Legend label="Order Requirements" />
                  <Form.Field<IBlurTextFieldProps>
                    dataid="orderQuantityMinimum"
                    component={BlurTextField}
                    label="Minimum Order Quantity"
                    type={BlurTextFieldType.Number}
                    decimals={getPrecision()}
                  />
                  <Form.Field<IBlurTextFieldProps>
                    dataid="orderQuantityIncrement"
                    component={BlurTextField}
                    label="Quantity Increment"
                    type={BlurTextFieldType.Number}
                    decimals={getPrecision()}
                  />
                </Form.Fieldset>
              </Form.Row>
              <Form.Row>
                <Form.Fieldset cols={1 / 2}>
                  <Form.Field<ICheckboxInputProps>
                    dataid="allowMatrix"
                    component={input.Checkbox}
                    temporary={true}
                    label="Allow Product Matrix"
                  />
                  {template ? null : (
                    <>
                      <Form.Legend label="Selling Prices" />
                      <Form.Row>
                        <Form.Fieldset cols={1 / 6} />
                        <Form.Fieldset cols={2 / 6}>
                          <ColumnLabel label="Standard" />
                        </Form.Fieldset>
                        <Form.Fieldset cols={2 / 6}>
                          <ColumnLabel label="VAT Inclusive" />
                        </Form.Fieldset>
                      </Form.Row>
                      <Form.Row>
                        <Form.Fieldset cols={1 / 6}>
                          <Label>Minimum Price</Label>
                        </Form.Fieldset>
                        <Form.Fieldset cols={2 / 6}>
                          <Form.Field<IBlurTextFieldProps>
                            dataid="sellingPrices.minimumPrice"
                            component={BlurTextField}
                            temporary={true}
                            readOnly={!!priceMatrix?.minimumPriceCalculation}
                            readOnlyMessage={`Calculated from ${getIn(
                              priceMatrix,
                              'minimumPriceCalculation.priceBase'
                            )}`}
                            includeLabel={false}
                            type={BlurTextFieldType.BaseCurrency}
                            valueType={'sales'}
                          />
                        </Form.Fieldset>
                        <Form.Fieldset cols={2 / 6}>
                          <Form.Field<IBlurTextFieldProps>
                            dataid="sellingPrices.minimumPriceRetail"
                            component={BlurTextField}
                            temporary={true}
                            includeLabel={false}
                            placeholder={`Calculated (${values.sellingPrices?.minimumPriceRetailPreview?.toFixed(2)})`}
                            type={BlurTextFieldType.BaseCurrency}
                            valueType={'sales'}
                          />
                        </Form.Fieldset>
                        <Form.Fieldset cols={1 / 6} />
                      </Form.Row>
                      {([1, 2, 3, 4, 5, 6, 7, 8, 9, 10] as Array<NumberRange<1, 10>>).map((priceIndex) => (
                        <PriceField
                          key={`price_${priceIndex}`}
                          priceIndex={priceIndex}
                          priceMatrix={priceMatrix}
                          productConfig={productConfig}
                          sellingPrices={values.sellingPrices}
                        />
                      ))}
                    </>
                  )}
                </Form.Fieldset>
                <Form.Fieldset cols={1 / 2}>
                  <Form.Legend label="Pricing Attributes" />
                  {([1, 2, 3, 4, 5, 6, 7, 8, 9, 10] as Array<NumberRange<1, 10>>).map((attributeIndex) => (
                    <PricingAttributeField
                      key={`attribute_${attributeIndex}`}
                      attributeIndex={attributeIndex}
                      pricingAttributes={pricingAttributes}
                    />
                  ))}
                </Form.Fieldset>
              </Form.Row>
            </Form.TabContent>
            <Form.TabContent id="units" label="Units">
              <Form.Row>
                <Form.Fieldset cols={1 / 2}>
                  <Form.Legend label="Selling Unit" />
                  <Form.Field<IBlurTextFieldProps>
                    dataid="precision"
                    component={BlurTextField}
                    label="Decimal Places"
                  />
                  <Form.Field<IBlurTextFieldProps>
                    dataid="weight"
                    component={BlurTextField}
                    label="Selling Unit Weight"
                  />
                  {productClass === ProductClass.Kit ? (
                    <Form.Field<ICheckboxInputProps>
                      dataid="calculateWeightFromComponents"
                      component={input.Checkbox}
                      label="Calculate Weight From Components"
                    />
                  ) : null}
                  <Form.Field<IBlurTextFieldProps>
                    dataid="volume"
                    component={BlurTextField}
                    label="Selling Unit Volume"
                  />
                </Form.Fieldset>
              </Form.Row>
              <VolumeFields />
              {fullUserDetails.flags.defaultUnitOfMeasure && (
                <Form.Row>
                  <Form.Fieldset cols={1}>
                    <Form.Row>
                      <Form.Fieldset cols={9 / 12} collapsed={true} />
                      <Form.Fieldset cols={1 / 12} collapsed={true}>
                        <ColumnLabel label="Default Selling Unit" />
                      </Form.Fieldset>
                      <Form.Fieldset cols={1 / 12} collapsed={true}>
                        <ColumnLabel label="Default Purchase Unit" />
                      </Form.Fieldset>
                    </Form.Row>
                    {StandardUnit()}
                  </Form.Fieldset>
                </Form.Row>
              )}
              <Form.Row>
                <Form.Fieldset cols={1}>
                  <Form.Legend label="Alternate Units Of Measure" />
                  <Form.Row>
                    <Form.Fieldset cols={2 / 12} collapsed={true}>
                      <ColumnLabel label="Description" />
                    </Form.Fieldset>
                    <Form.Fieldset cols={4 / 12} collapsed={true}>
                      <ColumnLabel label="Factor" />
                    </Form.Fieldset>
                    <Form.Fieldset cols={1 / 12} collapsed={true}>
                      <ColumnLabel label="Decimal Places" />
                    </Form.Fieldset>
                    <Form.Fieldset cols={2 / 12} collapsed={true}>
                      <ColumnLabel label="Barcode" />
                    </Form.Fieldset>
                    <Form.Fieldset cols={2 / 12} collapsed={true} />
                  </Form.Row>
                  <AlternateUnit unit={1} defaultUnitEnabled={fullUserDetails.flags.defaultUnitOfMeasure} />
                  <AlternateUnit unit={2} defaultUnitEnabled={fullUserDetails.flags.defaultUnitOfMeasure} />
                  <AlternateUnit unit={3} defaultUnitEnabled={fullUserDetails.flags.defaultUnitOfMeasure} />
                  <AlternateUnit unit={4} defaultUnitEnabled={fullUserDetails.flags.defaultUnitOfMeasure} />
                  <AlternateUnit unit={5} defaultUnitEnabled={fullUserDetails.flags.defaultUnitOfMeasure} />
                  <AlternateUnit unit={6} defaultUnitEnabled={fullUserDetails.flags.defaultUnitOfMeasure} />
                  <AlternateUnit unit={7} defaultUnitEnabled={fullUserDetails.flags.defaultUnitOfMeasure} />
                  <AlternateUnit unit={8} defaultUnitEnabled={fullUserDetails.flags.defaultUnitOfMeasure} />
                  <AlternateUnit unit={9} defaultUnitEnabled={fullUserDetails.flags.defaultUnitOfMeasure} />
                  <AlternateUnit unit={10} defaultUnitEnabled={fullUserDetails.flags.defaultUnitOfMeasure} />
                </Form.Fieldset>
              </Form.Row>
            </Form.TabContent>
            <Form.TabContent
              id="stockControl"
              label="Stock Control"
              shouldRender={productClass !== 'PROCESS'}
              permissionReference={'PRODUCTS_STOCK_CONTROL_WAREHOUSES_VIEW'}
            >
              <Form.Row>
                <Form.Fieldset cols={1 / 2}>
                  <Form.Legend label="GENERAL" />
                  <Form.Field<ICheckboxInputProps>
                    dataid="leveled"
                    component={input.Checkbox}
                    label="Maintain Levels"
                    readOnly={!!template}
                    hint={templateHint}
                    labelCols={1 / 2}
                    inputCols={1 / 2}
                  />
                  <Form.Field<ICheckboxInputProps>
                    dataid="nonReplenishable"
                    component={input.Checkbox}
                    label="Non Replenishable Batches"
                    readOnly={!!template}
                    hint={templateHint}
                    labelCols={1 / 2}
                    inputCols={1 / 2}
                  />
                  <Form.Field<ICheckboxInputProps>
                    dataid="batchAllocation"
                    component={input.Checkbox}
                    label="Batch Allocation"
                    readOnly={!values.leveled || values.valuationMethod === ValuationMethod.Fifo}
                    hint={'Should this product use the line-by-line batch allocation selection method on Sales Orders?'}
                    labelCols={1 / 2}
                    inputCols={1 / 2}
                  />
                  <Form.Field<ICheckboxInputProps>
                    dataid="noGroupByQuantity"
                    component={input.Checkbox}
                    label="Do Not Group By Quantity"
                    readOnly={!!template}
                    hint={'If enabled, purchase order lines generated from shortages will not be grouped'}
                    labelCols={1 / 2}
                    inputCols={1 / 2}
                  />
                  <Form.Field<IBlurTextFieldProps>
                    dataid="stockTakeAfter"
                    component={BlurTextField}
                    label="Stock Take After"
                    labelCols={1 / 2}
                    inputCols={1 / 2}
                  />
                </Form.Fieldset>
                <Form.Fieldset cols={1}>
                  <Form.Legend label="Warehouses" />
                  <GridContainer>
                    <Form.Field<IEditableDataGridProps>
                      dataid="productWarehouseDetails"
                      component={input.DataGrid}
                      columns={warehouseColumnMeta}
                      addBlankRow={false}
                      enableCellSelect={true}
                      enableCellAutoFocus={false}
                      contentHeight={window.innerHeight - 520}
                      hideLinesCondition={(rowData) => rowData.warehouse?.archived}
                      autoContentHeight={true}
                      disableVirtualRows={true}
                    />
                  </GridContainer>
                </Form.Fieldset>
              </Form.Row>
            </Form.TabContent>
            <Form.TabContent id="productAvailability" label="Availability" shouldRender={showExistingProductDetails}>
              <ProductAvailability product={values} />
            </Form.TabContent>
            <Form.TabContent id="vat" label="VAT/Nominal">
              <Form.Row>
                <Form.Fieldset cols={5 / 8}>
                  <Form.Legend label="VAT" />
                  <Form.Field<IDropdownProps>
                    dataid="vatProductCategory"
                    component={input.Dropdown}
                    label="Product VAT Category"
                    reformatLabel={false}
                  />
                </Form.Fieldset>
              </Form.Row>
              <Form.Row>
                <Form.Fieldset cols={5 / 8}>
                  <Form.Legend label="Nominal" />
                  <Form.Field<IDropdownProps>
                    dataid="nominalProductCategory"
                    component={input.Dropdown}
                    label="Nominal Product Category"
                  />
                  <Form.Field<IDropdownProps>
                    dataid="nominalProductAccountSet"
                    component={input.Dropdown}
                    label="Product Account Set"
                  />
                </Form.Fieldset>
              </Form.Row>
              <Form.Row>
                <Form.Fieldset cols={5 / 8}>
                  <Form.Legend label="Miscellaneous" />
                  <Form.Field<IBlurTextFieldProps>
                    dataid="commodityCode"
                    component={BlurTextField}
                    label="Commodity Code"
                  />
                  <Form.Field<IBlurTextFieldProps>
                    dataid="commodityDescription"
                    component={BlurTextField}
                    label="Commodity Description"
                    hint="Brief description of the product"
                  />
                  <Form.Field<IBlurTextFieldProps>
                    dataid="commodityComposition"
                    component={BlurTextField}
                    label="Commodity Composition"
                    hint="Composition/materials used to manufacture"
                  />
                  <Form.Field<IDropdownProps>
                    dataid="countryOfOrigin"
                    component={input.Dropdown}
                    label="Country of Origin"
                  />
                  <Form.Field<ICheckboxInputProps>
                    dataid="useSupplementaryUnits"
                    component={input.Checkbox}
                    label="Supplementary Units"
                  />
                  {useSupplementaryUnits && (
                    <>
                      <Form.Field<IDropdownProps>
                        dataid="supplementaryUnit"
                        component={BlurTextField}
                        label="Supplementary Unit"
                      />
                      <Form.Field<IDropdownProps>
                        dataid="supplementaryUnitMultiplier"
                        component={BlurTextField}
                        label="Supplementary Unit Multiplier"
                      />
                    </>
                  )}
                  {windsorFrameworkEnabled && (
                    <Form.Field<IDropdownProps>
                      dataid="windsorFrameworkAtRiskStatus"
                      component={input.Dropdown}
                      label="Windsor Framework At Risk"
                    />
                  )}
                </Form.Fieldset>
              </Form.Row>
            </Form.TabContent>
            <Form.TabContent id="attachments" label="Attachments" badge={attachmentsCount} onlyRenderWhenActive={true}>
              <Form.Row>
                <Form.Fieldset cols={1}>
                  <Form.Legend label="Main image" />
                  <Form.Field<any> dataid="image.fileName" component={input.ImageSelector} includeLabel={false} />
                </Form.Fieldset>
              </Form.Row>
              <Form.Row>
                <Form.Fieldset cols={1}>
                  <Form.Legend label="Attachments" />
                  <Form.Field<IGridUploaderProps>
                    dataid="attachments"
                    component={input.GridUploader}
                    additionalColumns={[
                      {
                        name: 'Safety Data Sheet',
                        key: 'safetyDataSheet',
                        widthFactor: 1,
                        formatter: Formatters.CheckFormatter,
                        editor: <AnyCheckboxEditor />,
                        editable: true,
                        resizable: true,
                        draggable: false,
                      },
                      {
                        name: 'Attach on Quotes and SO Documents',
                        key: 'includeOnSalesOrderDocuments',
                        widthFactor: 1,
                        formatter: Formatters.CheckFormatter,
                        editor: <AnyCheckboxEditor />,
                        editable: true,
                        resizable: true,
                        draggable: false,
                      },
                      {
                        name: 'Attach on PO Documents',
                        key: 'includeOnPurchaseOrderDocuments',
                        widthFactor: 1,
                        formatter: Formatters.CheckFormatter,
                        editor: <AnyCheckboxEditor />,
                        editable: true,
                        resizable: true,
                        draggable: false,
                      },
                      {
                        name: 'Export to Web',
                        key: 'exportToWeb',
                        widthFactor: 1,
                        formatter: Formatters.CheckFormatter,
                        editor: <AnyCheckboxEditor />,
                        editable: true,
                        resizable: true,
                        draggable: false,
                      },
                      {
                        name: 'Updated At',
                        key: 'updatedAt',
                        widthFactor: 1,
                        editable: false,
                        resizable: true,
                        draggable: false,
                        formatter: Formatters.DateTimeFormatter,
                      },
                    ]}
                  />
                </Form.Fieldset>
              </Form.Row>
            </Form.TabContent>
            <Form.TabContent
              id="components"
              label="Components"
              shouldRender={productClass === ProductClass.Kit || productClass === ProductClass.Assembly}
            >
              <Form.Row>
                <Form.Fieldset cols={1}>
                  <CommentRowContext.Provider
                    value={{
                      selectRow: (rowIdx: number) => setAddCommentToRowIdx(rowIdx),
                      addCommentToRowIdx: addCommentToRowIdx,
                      dataIdPrefix: `components`,
                    }}
                  >
                    <Form.Field<IEditableDataGridProps>
                      dataid="components"
                      component={input.DataGrid}
                      columns={componentColumnMeta}
                      addBlankRow={true}
                      enableCellSelect={true}
                      enableCellAutoFocus={false}
                      dataStoreId={PRODUCT_FORM_DATA_STORE_ID}
                      contentHeight={window.innerHeight - 520}
                      rowRenderer={LazyCommentRowRenderer}
                      enableContextMenu={true}
                      renderActionButtons={renderActionButtons}
                      temporaryOnChange={true}
                      contextKey={'components'}
                      contentWidth={window.innerWidth - 320}
                      onCellSelected={(_0, _1, rowData) => {
                        setComponentsTabProductId(rowData?.component?.id);
                      }}
                    />
                  </CommentRowContext.Provider>
                </Form.Fieldset>
              </Form.Row>
              {componentsTabProductId ? <StockLevelTab productId={componentsTabProductId} /> : null}
            </Form.TabContent>
            <Form.TabContent id="assemblies" label="Assemblies" shouldRender={productClass === ProductClass.Assembly}>
              <LazyAssemblyWorkspace
                viewScreenName="product-assemblies"
                embeddedView
                allowView={false}
                predefinedFilters={[
                  {
                    filterOperation: FilterOperation.Equals,
                    filterParameters: {
                      field: 'product.id',
                      value: values?.id,
                    },
                  },
                ]}
              />
            </Form.TabContent>
            <Form.TabContent id="componentUsage" label="Component Usage" shouldRender={!props.newProduct}>
              <Form.Row>
                <Form.Fieldset cols={1}>
                  <GridContainer>
                    <Query
                      query={productComponentDirectAncestors}
                      errorPolicy={'all'}
                      variables={{ productId: id }}
                      skip={!props.initialized}
                      fetchPolicy="network-only"
                    >
                      {({ loading, data }: { loading: boolean; data: any }) => {
                        if (loading) {
                          return null;
                        }
                        if (!data) {
                          return null;
                        }

                        const ancestors = data.data || [];
                        return (
                          <EditableDataGrid
                            columns={productAncestorColumnMeta}
                            addBlankRow={false}
                            enableCellSelect={true}
                            enableCellAutoFocus={false}
                            listData={ancestors}
                            rowsCount={ancestors.length}
                            contentHeight={window.innerHeight - 250}
                            contextKey={'componentUsage'}
                          />
                        );
                      }}
                    </Query>
                  </GridContainer>
                </Form.Fieldset>
              </Form.Row>
            </Form.TabContent>
            <Form.TabContent
              id="componentReturns"
              label="Component Returns"
              shouldRender={productClass === ProductClass.Assembly}
            >
              <Form.Row>
                <Form.Fieldset cols={1}>
                  <Form.Field<IEditableDataGridProps>
                    dataid="componentReturns"
                    component={input.DataGrid}
                    columns={componentReturnColumnMeta}
                    addBlankRow={true}
                    enableCellSelect={true}
                    enableCellAutoFocus={false}
                    dataStoreId={PRODUCT_FORM_DATA_STORE_ID}
                    contentHeight={window.innerHeight - 520}
                    enableContextMenu={true}
                    contextKey={'components'}
                    contentWidth={window.innerWidth - 320}
                  />
                </Form.Fieldset>
              </Form.Row>
              {componentsTabProductId ? <StockLevelTab productId={componentsTabProductId} /> : null}
            </Form.TabContent>
            <Form.TabContent id="aliases" label="Aliases">
              <Form.Row>
                <Form.Fieldset cols={1}>
                  <Form.Field<any>
                    dataid="alias"
                    component={input.DisplayList}
                    objectKey="reference"
                    label="Product Alias"
                    spellCheck="false"
                  />
                </Form.Fieldset>
              </Form.Row>
            </Form.TabContent>
            <Form.TabContent id="suppliersTab" label="Suppliers" permissionReference={'SUPPLIERS_VIEW'}>
              <Form.Row>
                <Form.Fieldset cols={1}>
                  <ProductSupplierConfig {...props} productConfig={productConfig} forceUpdate={true} />
                </Form.Fieldset>
              </Form.Row>
            </Form.TabContent>
            <Form.TabContent
              id="analytics"
              label="Analytics"
              key="analysis"
              onlyRenderWhenActive={true}
              shouldRender={!props.newProduct && !preview}
              permissionReference={'SALES_LEDGER_ANALYTICS'}
            >
              <SalesInvoiceAnalysis
                predefinedFilters={predefinedAnalysisFilters}
                embedded={EmbeddedSalesInvoiceOptions.Product}
              />
            </Form.TabContent>
            <Form.TabContent
              id="notesTab"
              label="Notes"
              badge={memoEditorProps.queryProps.data?.listData?.count}
              type="info"
              onlyRenderWhenActive={true}
              shouldRender={!props.newProduct}
            >
              <MemoEditor noteTypeEntity={ProductNoteType} {...memoEditorProps} />
            </Form.TabContent>
            <Form.TabContent id="alternativeProducts" label="Alternative Products">
              <Form.Fieldset cols={1}>
                <Form.Field<IEditableDataGridProps>
                  dataid="supersessions"
                  component={input.DataGrid}
                  columns={alternativeProductColumnMeta}
                  addBlankRow={true}
                  enableCellSelect={true}
                  enableCellAutoFocus={false}
                  dataStoreId={PRODUCT_FORM_DATA_STORE_ID}
                  contentHeight={window.innerHeight - 250}
                  deleteOption={onRowDeleteClick}
                />
              </Form.Fieldset>
            </Form.TabContent>
            <Form.TabContent id="channels" label="Channels">
              <Form.Row>
                <Form.Fieldset cols={5 / 8}>
                  <Form.Legend label="Generic Channels" />
                  <Form.Field<any>
                    dataid="channels"
                    component={input.SelectList}
                    query={ChannelList}
                    type="selectlist"
                    optionLabelField="description"
                    enabledField="enabled"
                    showHighlight={false}
                  />
                </Form.Fieldset>
              </Form.Row>
              <Form.Row>
                <Form.Fieldset cols={5 / 8}>
                  <Form.Legend label="eBay Channels" />
                  <Form.Field<IEditableDataGridProps>
                    dataid="channelsEbay"
                    component={input.DataGrid}
                    columns={ebayChannelColumnMeta}
                    addBlankRow={true}
                    enableCellSelect={true}
                    enableCellAutoFocus={false}
                    autoContentHeight={true}
                  />
                </Form.Fieldset>
              </Form.Row>
              <Form.Row>
                <Form.Fieldset cols={5 / 8}>
                  <Form.Legend label="Amazon Channels" />
                  <Form.Field<IEditableDataGridProps>
                    dataid="channelsAmazon"
                    component={input.DataGrid}
                    columns={amazonChannelColumnMeta}
                    addBlankRow={true}
                    enableCellSelect={true}
                    enableCellAutoFocus={false}
                    autoContentHeight={true}
                  />
                </Form.Fieldset>
              </Form.Row>
              <Form.Row>
                <Form.Fieldset cols={5 / 8}>
                  <Form.Legend label="Shopify Channels" />
                  <Form.Field<IEditableDataGridProps>
                    dataid="channelsShopify"
                    component={input.DataGrid}
                    columns={shopifyChannelColumnMeta}
                    addBlankRow={true}
                    enableCellSelect={true}
                    enableCellAutoFocus={false}
                    autoContentHeight={true}
                  />
                </Form.Fieldset>
              </Form.Row>
              <Form.Row>
                <Form.Fieldset cols={5 / 8}>
                  <Form.Legend label="WooCommerce Channels" />
                  <Form.Field<IEditableDataGridProps>
                    dataid="channelsWooCommerce"
                    component={input.DataGrid}
                    columns={wooCommerceChannelColumnMeta}
                    addBlankRow={true}
                    enableCellSelect={true}
                    enableCellAutoFocus={false}
                    autoContentHeight={true}
                  />
                </Form.Fieldset>
              </Form.Row>
              <Form.Row>
                <Form.Fieldset cols={5 / 8}>
                  <Form.Legend label="Etsy Channels" />
                  <Form.Field<IEditableDataGridProps>
                    dataid="channelsEtsy"
                    component={input.DataGrid}
                    columns={etsyChannelColumnMeta}
                    addBlankRow={true}
                    enableCellSelect={true}
                    enableCellAutoFocus={false}
                    autoContentHeight={true}
                  />
                </Form.Fieldset>
              </Form.Row>
              <Form.Row>
                <Form.Fieldset cols={5 / 8}>
                  <Form.Legend label="BigCommerce Channels" />
                  <Form.Field<IEditableDataGridProps>
                    dataid="channelsBigCommerce"
                    component={input.DataGrid}
                    columns={bigCommerceChannelColumnMeta}
                    addBlankRow={true}
                    enableCellSelect={true}
                    enableCellAutoFocus={false}
                    autoContentHeight={true}
                  />
                </Form.Fieldset>
              </Form.Row>
              <Form.Row>
                <Form.Fieldset cols={5 / 8}>
                  <Form.Legend label="Magento Channels" />
                  <Form.Field<IEditableDataGridProps>
                    dataid="channelsMagento"
                    component={input.DataGrid}
                    columns={magentoChannelColumnMeta}
                    addBlankRow={true}
                    enableCellSelect={true}
                    enableCellAutoFocus={false}
                    autoContentHeight={true}
                  />
                </Form.Fieldset>
              </Form.Row>
            </Form.TabContent>
            <Form.TabContent
              id="stockLevel"
              label="Stock Level"
              key="stockLevelTab"
              shouldRender={!props.newProduct && productClass !== 'PROCESS' && !preview}
            >
              <StockLevelTab />
            </Form.TabContent>
            <Form.TabContent
              id="salesHistory"
              label="Sales History"
              key="salesTab"
              shouldRender={showExistingProductDetails}
              permissionReference={'SALES_ORDERS_VIEW'}
            >
              <GridContainer>
                <SalesInvoiceLineWorkspace
                  embeddedView={true}
                  pageView={props.pageView}
                  viewScreenName="/salesLedger/InvoiceLines/Modal"
                  predefinedFilters={transactionPredefinedFilters}
                  editInNewTab={true}
                />
              </GridContainer>
            </Form.TabContent>
            <Form.TabContent
              id="salesActivity"
              label="Sales & Quotes"
              key="salesTab"
              shouldRender={showExistingProductDetails}
              permissionReference={'SALES_ORDERS_VIEW'}
            >
              <GridContainer>
                <SalesOrderLineWorkspace
                  embeddedView={true}
                  pageView={props.pageView}
                  viewScreenName="/sales/Orders/Modal"
                  predefinedFilters={transactionPredefinedFilters}
                  editInNewTab={true}
                />
              </GridContainer>
            </Form.TabContent>
            <Form.TabContent
              id="purchases"
              label="Purchase History"
              key="purchasesTab"
              shouldRender={showExistingProductDetails}
              permissionReference={'PURCHASING_ORDERS_VIEW'}
            >
              <GridContainer>
                <PurchaseOrderLineWorkspace
                  embeddedView={true}
                  pageView={props.pageView}
                  viewScreenName="/purchases/OrderLines/Modal"
                  predefinedFilters={transactionPredefinedFilters}
                  editInNewTab={true}
                />
              </GridContainer>
            </Form.TabContent>
            <Form.TabContent
              id="transactions"
              label="Transactions"
              key="transactionsTab"
              shouldRender={showExistingProductDetails}
              permissionReference={'PRODUCTS_STOCK_TRANSACTIONS_VIEW'}
            >
              <GridContainer>
                <StockTransactionWorkspace
                  embeddedView={true}
                  pageView={props.pageView}
                  additionalVariables={{ excludeProduct: true }}
                  predefinedFilters={transactionPredefinedFilters}
                  viewScreenName="/Products/StockTransaction/Modal"
                  precision={getPrecision()}
                />
              </GridContainer>
            </Form.TabContent>
            <Form.TabContent
              id="bins"
              label="Bin Locations"
              key="binLocationsTab"
              shouldRender={showExistingProductDetails}
            >
              <GridContainer>
                <ProductsStockLotWorkspace
                  embeddedView={true}
                  additionalVariables={{ excludeProduct: true }}
                  predefinedFilters={stockLotsPredefinedFilters}
                  viewScreenName="/Products/StockLots/Modal"
                  includeGenericSearch={false}
                  precision={getPrecision()}
                  columns={stockLotsWorkspaceColumns}
                  nonReplenishable={nonReplenishable}
                  contentHeight={window.innerHeight - (selectedStockLot ? 500 : 0)}
                  onRowClick={(value) => {
                    setSelectedStockLot(value);
                  }}
                />
              </GridContainer>
              {selectedStockLot && (
                <>
                  <Form.Legend label="Selected Stock Lot Attachments" />
                  <GridUploader value={selectedStockLot?.attachments} onChange={onAttachmentChange} />
                </>
              )}
            </Form.TabContent>
            <Form.TabContent
              id="audit"
              label="Audit"
              key="auditTab"
              shouldRender={showExistingProductDetails}
              permissionReference={'PRODUCTS_AUDIT_VIEW'}
            >
              <GridContainer>
                <AuditByFieldWorkspace
                  embeddedView={true}
                  pageView={props.pageView}
                  predefinedFilters={productAuditPredefinedFilters}
                  viewScreenName="/product/ProductAudit/Workspace"
                  entity={ProductAuditByField}
                />
              </GridContainer>
            </Form.TabContent>
            <Form.TabContent
              id="customer_part_numbers"
              label="Customer Part Numbers"
              key={'customer_part_numbers'}
              shouldRender={showExistingProductDetails}
            >
              <Form.Row>
                <Form.Field<IEditableDataGridProps>
                  addBlankRow={true}
                  dataid="customerPartNumbers"
                  enableCellSelect={!values?.archived}
                  enableCellAutoFocus={false}
                  columns={defineColumnMeta()}
                  component={input.DataGrid}
                  contentHeight={750}
                  temporaryOnChange={true}
                  temporaryOnFocus={() => true}
                  enableContextMenu={true}
                  deleteOption={deleteCustomerPartNumber}
                  contextKey={'customerPartNumbers'}
                />
              </Form.Row>
            </Form.TabContent>
            <Form.TabContent id="product_alerts" label="Alerts">
              <Form.Field<any>
                dataid={'formAlerts'}
                component={input.GenericArray}
                label="Alerts"
                renderRow={FormAlertRow}
                renderDetails={FormAlertDetails}
                enableNewRows={true}
                forceUpdate={true}
                renderRowIsComponent={true}
                detailProps={{ type: 'Product' }}
              />
            </Form.TabContent>
            <Form.TabContent id="purchase_requirement_details" label="Shortages" permissionReference={'SHORTAGES_VIEW'}>
              <LazyPurchaseRequirementDetailWorkspace
                embeddedView={true}
                predefinedFilters={[
                  values
                    ? {
                        filterOperation: FilterOperation.Equals,
                        filterParameters: { field: 'product.id', value: values.id },
                      }
                    : null,
                ]}
                overrideParams={{ id: 'ALL' }}
                viewScreenName={'/Products/Product/Details/Shortages'}
              />
            </Form.TabContent>
            <Form.TabContent
              id="allocations"
              label="Allocations"
              onlyRenderWhenActive={true}
              shouldRender={!props.newProduct}
              permissionReference={'SALES_LEDGER_ALLOCATIONS_VIEW'}
            >
              <GridContainer>
                <LazyProductAllocationWorkspace
                  embeddedView={true}
                  predefinedFilters={[
                    {
                      filterParameters: {
                        field: 'stockLot.stock.product.id',
                        value: values.id,
                      },
                      filterOperation: FilterOperation.Equals,
                    },
                    {
                      filterParameters: {
                        field: 'cancelled',
                        value: 'false',
                      },
                      filterOperation: FilterOperation.Equals,
                    },
                  ]}
                  viewScreenName={`${Product.formUrl}Allocation/`}
                />
              </GridContainer>
            </Form.TabContent>
            <Form.TabContent
              id={'reservations'}
              label={'Reservations'}
              onlyRenderWhenActive={true}
              shouldRender={!props.newProduct}
            >
              <Suspense fallback={<ProgressIndicator />}>
                <GridContainer>
                  <LazyReservationWorkspace
                    embeddedView={true}
                    predefinedFilters={[
                      {
                        filterParameters: {
                          field: 'productComponent.component.id',
                          value: values.id,
                        },
                        filterOperation: FilterOperation.Equals,
                      },
                      {
                        filterParameters: {
                          field: 'processed',
                          value: 'false',
                        },
                        filterOperation: FilterOperation.Equals,
                      },
                      {
                        filterParameters: {
                          field: 'quantity',
                          value: '0',
                        },
                        filterOperation: FilterOperation.GreaterThan,
                      },
                    ]}
                    viewScreenName={`${Product.formUrl}Reservation/`}
                  />
                </GridContainer>
              </Suspense>
            </Form.TabContent>
            <Form.TabContent
              id={'stockCommitments'}
              label={'Stock Commitments'}
              onlyRenderWhenActive={true}
              shouldRender={!props.newProduct}
            >
              <Suspense fallback={<ProgressIndicator />}>
                <GridContainer>
                  <LazyStockCommitmentWorkspace
                    embeddedView={true}
                    predefinedFilters={[
                      {
                        filterParameters: {
                          field: 'product.id',
                          value: values.id,
                        },
                        filterOperation: FilterOperation.Equals,
                      },
                      {
                        filterParameters: {
                          field: 'processed',
                          value: 'false',
                        },
                        filterOperation: FilterOperation.Equals,
                      },
                      {
                        filterParameters: {
                          field: 'quantity',
                          value: '0',
                        },
                        filterOperation: FilterOperation.GreaterThan,
                      },
                    ]}
                    viewScreenName={`${Product.formUrl}Reservation/`}
                  />
                </GridContainer>
              </Suspense>
            </Form.TabContent>
            <Form.TabContent id="web" label="Web">
              <Form.Row>
                <Form.Fieldset cols={1}>
                  <Form.Legend label="Web" />
                  <Form.Field<IBlurTextFieldProps> dataid="webTitle" component={BlurTextField} label="Title" />
                  <Form.Field<IBlurTextFieldProps>
                    dataid="webShortTitle"
                    component={BlurTextField}
                    label="Short Title"
                  />
                  <Form.Field dataid="webStatus" component={input.Checkbox} label="Enabled" />
                  <Form.Field<IBlurTextFieldProps>
                    dataid="metaDescription"
                    component={BlurTextField}
                    label="Meta Description"
                  />
                  <Form.Field<IBlurTextFieldProps>
                    dataid="searchKeywords"
                    component={BlurTextField}
                    label="Search Keywords"
                  />
                  <Form.Legend label="Description" />
                  <Wysiwyg
                    ref={wysiwygRef}
                    value={values.webDescription ?? ''}
                    customFileSearchComponent={WebMediaFolderTreespace}
                    customStyling={company?.wysiwygEditorCss}
                    staticBlocks={staticBlocks}
                    onRemoteBlockSearch={debouncedSearch}
                    onMergedContentRequested={onMergedContentRequested}
                  />
                </Form.Fieldset>
              </Form.Row>
            </Form.TabContent>
            <Form.TabContent id="epr" label="EPR" onlyRenderWhenActive={true}>
              <Form.Row>
                <Form.Fieldset cols={1 / 2} style={{ marginTop: '1em' }}>
                  <Dropdown
                    testId="epr-unit-description"
                    options={eprUnitOptions}
                    label="Unit Description"
                    onChange={(value: IUnitOfMeasureEmbeddable) => {
                      setSelectedEprUnit(value);
                    }}
                    value={selectedEprUnit}
                    labelKey="description"
                  />
                </Form.Fieldset>
                <Form.Fieldset cols={1 / 2}>
                  <Button
                    primary={true}
                    data-testid="epr-unit-duplicate-btn"
                    onClick={onConfirmDuplicateMaterialDetails}
                    disabled={
                      !selectedEprUnit ||
                      selectedEprUnit?.description === props.values?.standardSellingUnit?.description
                    }
                  >
                    Duplicate{' '}
                    {props.values?.standardSellingUnit?.description &&
                      selectedEprUnit?.description !== props.values?.standardSellingUnit?.description &&
                      ` '${props.values?.standardSellingUnit?.description}'`}
                  </Button>
                </Form.Fieldset>
              </Form.Row>
              <Form.Row>
                <Form.Fieldset cols={1}>
                  <EprContext.Provider value={{ selectedEprUnit }}>
                    <Form.Field<IEditableDataGridProps>
                      dataid="productMaterialDetails"
                      component={input.DataGrid}
                      columns={PRODUCT_MATERIAL_DETAIL_COLUMNS}
                      addBlankRow={true}
                      enableCellSelect={true}
                      enableContextMenu={true}
                      rowRenderer={EprRowRender}
                      contextKey={'productMaterialDetails'}
                      contentWidth={window.innerWidth - 320}
                      deleteOption={(rowIdx) => {
                        dispatch(props.change(`productMaterialDetails.${rowIdx}.deleted`, true));
                      }}
                    />
                  </EprContext.Provider>
                </Form.Fieldset>
              </Form.Row>
            </Form.TabContent>
          </Form.TabPanel>
        </Form.Form>
      </Form.FormContainer>
    </div>
  );
};

const getDefaultVariables = (location: any) => {
  const url = location.pathname || '';
  let isTemplate = url.startsWith('/Products/ProductTemplate');

  location.state?.defaultValues?.forEach?.((defaultValue: any) => {
    if (defaultValue.field === 'template') {
      isTemplate = defaultValue.value;
    } else {
      console.warn('Unhandled default field.');
    }
  });

  return { newProductIsTemplate: isTemplate };
};

type IConnectedProductFormProps = {
  dispatch: Dispatch;
  productConfig: Maybe<IProductConfig>;
  company: Maybe<ICompany>;
  newProduct: boolean;
  productId: number;
  productFilters: IProductPredefinedFilters;
  app: any;
};

const ConnectedProductForm: FunctionComponent<IConnectedFormProps & IConnectedProductFormProps> = createForm({
  form: type,
  name: formTitle,
  entity: Product,
  queryFragment: fragment,
  defaultReturnUrl: workspaceUrl,
  forceUpdate: true,
})(ProductForm) as any;

//Using a wrapper to get hooks access and to abstract out some of the 'this.something' uses in the form.
export default ({
  temporaryDefault,
  overrideParams,
  detailView,
  ...props
}: Omit<IProductFormProps, 'change' | 'values' | 'dispatch' | 'onChange' | 'connectedForm' | 'productFilters'> & {
  temporaryDefault?: boolean;
  overrideParams: any;

  /**
   * This prop indicates that the form is displaying within a Workspace in "Form View".
   */
  detailView?: boolean;
}) => {
  const formProps = useSelector((store) => mapStateToFormProps(store, props));
  const location = useLocation();
  const formDefault = useFormQuery(
    (temporaryDefault
      ? { default: temporary, instance: instance }
      : { default: defaultQuery, instance: instance }) as EntityType,
    temporaryDefault
      ? { forceDefault: true, idParam: 'uuid', overrideParams }
      : { duplicate: true, defaultVariables: getDefaultVariables(location), overrideParams }
  );

  const cacheUpdate = useEntityCacheUpdate('products', 'product', { detailView });
  const [formUpdate] = useMutation<IProductMutationResult, IProductMutationVariables>(update as DocumentNode, {
    update: cacheUpdate,
    refetchQueries: [ProductNotesWorkspaceQuery],
  });
  const [formTemporaryUpdate] = useMutation(temporaryUpdate as DocumentNode);
  const dispatch = useDispatch();

  const productConfig: Maybe<IProductConfig> = useProductConfig();
  //NOTE: company is only current used in ProductOverviewTab - Should make that functional and do useCompany there instead.
  const company: Maybe<ICompany> = useCompany();
  const params = useParams<{ id?: string }>();

  const id = useMemo(() => overrideParams?.id || params.id, [overrideParams?.id, params.id]);
  const newProduct = !(id && id !== 'New');
  const productId = newProduct ? null : id;
  const filters = useRef<IProductPredefinedFilters>({});
  const app = useContext(AppContext);

  useEffect(() => {
    filters.current = createProductFilters(productId);
  }, [productId]);

  return (
    <ConnectedProductForm
      {...formProps}
      {...props}
      formUpdate={formUpdate as any}
      formTemporaryUpdate={formTemporaryUpdate}
      formData={formDefault}
      dispatch={dispatch}
      productConfig={productConfig}
      company={company}
      newProduct={newProduct}
      productId={productId}
      productFilters={filters.current}
      app={app}
    />
  );
};
